import React, { useState, useEffect, useRef, Fragment, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useBoardFullData } from "./hooks/useBoardFullData";
import { useBoardNotices } from "./hooks/useBoardNotices";
import { useDateRange } from "./hooks/useDateRange";
import { useBoardWebSocket } from "./hooks/useBoardWebSocket";
import { getWebSocketClient } from "../../lib/websocket/boardWebSocket";
import { useBoardStore, BoardChannel, BoardJournal } from "../../stores/boardStore";
import { useAuthStore } from "../../stores/authStore";
import { useJournalStore } from "../../stores/journalStore";
import { useOnboardingStore, OnboardingStep } from "../../stores/onboardingStore";
import { SafeImage } from "../../components/common/SafeImage";

import DatePicker from "../../components/DatePicker";
import { BoardSetupSteps } from "./components/BoardSetupSteps";
import {
  BoardMainSpace,
  ChannelEntityModal,
  AddChannelModal,
  BoardPagination,
  BoardCurrencyButton,
  BoardContentModal,
  BoardSpecModal,
  FeatureExplanationModal,
  StackedNoticeBanners,
  PageTypeSelectionModal,
} from "./components";
import { PageRenderer, isCustomPage } from "./components/pages/PageRenderer";
import { PageType, pageTypeRequiresTemplates } from "./components/modals/PageTypeSelectionModal";
import { BoardCurrencyModal } from "./components/modals/BoardCurrencyModal";
import { BoardChartsModal } from "./components/modals/BoardChartsModal";
import { BoardTemplatesModal } from "./components/modals/BoardTemplatesModal";
import { BoardSaveTemplateModal } from "./components/modals/BoardSaveTemplateModal";
import { SelectConnectionTypeModal } from "./components/modals/SelectConnectionTypeModal";
import { SelectChannelSourceModal } from "./components/modals/SelectChannelSourceModal";
import { PseudoChannelPickerModal } from "./components/modals/PseudoChannelPickerModal";
import { SelectJournalSourceModal } from "./components/modals/SelectJournalSourceModal";
import { BoardJournalDetailsModal } from "./components/modals/BoardJournalDetailsModal";
import { EditJournalNameModal } from "./components/modals/EditJournalNameModal";
import { InfoModal } from "./components/modals/InfoModal";

import { GridItem } from "./components/BoardGrid";
import { CHART_DEFINITIONS } from "./components/charts";
import {
  convertGridItemsToStorage,
  convertStorageToGridItems,
  detectBreakpoint,
  calculateChartWidth,
  calculateChartHeight,
  findPositionForNewItem,
  compactHorizontal,
  generateResponsiveLayouts
} from "./components/charts/chartStorage";
import { SIZE_TIERS, CHART_WIDTH_MAP, SizeTier } from "./components/charts/types";
import { Layout } from "react-grid-layout";
import { Channel, channelsService } from "../../lib/api/channels";
import { boardService } from "../../lib/api/boards";
import { useAlertStore } from "../../stores/alertStore";
import { aiService } from "../../lib/api/ai";
import { subscriptionsService } from "../../lib/api/subscriptions";
import { buildShopifyManageBillingUrl } from "../../lib/shopifyAdminUrl";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { AITaskConfirmationModal } from "./components/modals/AITaskConfirmationModal";
import { useUserChannelsStore } from "../../stores/userChannelsStore";
import { Logo } from "../../components/common/Logo";
import { AnimatedLogo } from "../../components/common/AnimatedLogo";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Crown, Pencil, Check, X, Plus, RotateCcw, AlertCircle, ShoppingBag } from 'lucide-react';

// Mirrors backend/src/routes/ai.js — assistant messages with this exact
// content represent a failed turn and are rendered as the error+retry UI.
const AI_ERROR_SENTINEL = "__DASHBAL_AI_ERROR__";

// Internal user message persisted by the backend after a plan is approved so
// the LLM knows the prior task batch has been executed. It's not real user
// input — keep it in the DB for context but hide it from the chat UI.
const AI_APPROVAL_USER_MARKER = "Plan Execution Approved.";
import { UpgradeModal } from '../subscription/UpgradeModal';
import { usePlanFeaturesStore } from '../../stores/planFeaturesStore';

const BoardTitleLoadingText = () => {
  const { t } = useTranslation("common");
  const loadingStates = React.useMemo(() => [
    t('loadingStates.fetching'),
    t('loadingStates.organizing'),
    t('loadingStates.analyzing'),
    t('loadingStates.syncing'),
    t('loadingStates.almost'),
    t('loadingStates.finalizing')
  ], [t]);
  const [msgIndex, setMsgIndex] = React.useState(0);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const scheduleNext = () => {
      const delay = Math.floor(Math.random() * (7000 - 3000 + 1)) + 3000;
      timeoutId = setTimeout(() => {
        setMsgIndex((prev) => (prev + 1) % loadingStates.length);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [loadingStates.length]);

  const displayMsg = loadingStates[msgIndex % loadingStates.length];

  return (
    <div className="overflow-hidden h-4 flex items-center">
      <AnimatePresence mode="wait">
        <motion.span
          key={displayMsg}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 8 }}
          transition={{ duration: 0.25 }}
          className="text-[11px] font-bold text-accent-primary whitespace-nowrap"
        >
          {displayMsg.replace(/\.+$/, '')}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

const BoardPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const boardNameFromState = location.state?.boardName;
  const { showAlert } = useAlertStore();
  const { completeStep } = useOnboardingStore();
  const queryClient = useQueryClient();

  // Supported Channels logic for Header
  const { data: channelTypesData } = useQuery({
    queryKey: ["channelTypes"],
    queryFn: channelsService.getChannelTypes,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const supportedChannels = (channelTypesData?.data || []).filter(c => c.isActive !== false);

  // Billing source — used to route the AI-quota "Upgrade" CTA to Shopify
  // Admin instead of /plan (which would just show the Shopify wall anyway).
  const { data: billingStatusResponse } = useQuery({
    queryKey: ["subscriptions", "billing-status"],
    queryFn: subscriptionsService.getBillingStatus,
    retry: false,
    staleTime: 60 * 1000,
  });
  const billingStatus = billingStatusResponse?.data;
  const shopifyManageUrl =
    billingStatus?.billingSource === "shopify" &&
    billingStatus?.billingStatus === "active"
      ? buildShopifyManageBillingUrl(billingStatus?.shopifyBillingShop)
      : null;

  const CDN_URL = (((window as any).__DASHBAL_CONFIG__?.cdnUrl) || 'https://cdn.dashbal.com').replace(/\/$/, '');

  // Constants for other header buttons
  const JOURNAL_CONFIG = { id: 'journal', name: t('boards.moneyJournal', 'Money Journal'), icon: `${CDN_URL}/logos/internal/money_journal.svg` };
  const SPEC_CONFIG = { id: 'spec', name: t('boards.boardSpec', 'Board Spec'), icon: `${CDN_URL}/logos/internal/board_spec.svg` };

  // Datepicker visibility state for onboarding guide auto-hide
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const {
    channels,
    channelErrors,
    selectedEntities,
    dateRange,
    isDataLoading,
    finance,
    pages,
    activePageId,
    journals,
    currencySettings,
    usedCurrencies,
    specs,
    setSpecs,
    clearBoardState,
    setBoardData,
    setCurrencySettings,
    setActivePage,
    addPage,
    updatePage,
    addJournal,
    deleteJournal,
    setIsDataLoading,
    setHasAITasks,
  } = useBoardStore();

  const activePage = pages.find(p => p.id === activePageId);
  const activePageCharts = Array.isArray(activePage?.chartConfig) ? activePage.chartConfig : [];

  const boardFullResponse = useBoardFullData(id || '', activePageCharts, activePage?.type);

  const { updateChannel: updateUserChannel, loadChannels: loadUserChannels } = useUserChannelsStore();
  const { isFinanceLoading, isFinanceError } = boardFullResponse;
  const board = boardFullResponse.data?.data?.board;

  // Each BoardChannel row is its own connection — pseudo siblings of the same
  // real channel come through as distinct rows (with their own `name`) from the
  // backend, so simple grouping by channelType suffices.
  const connectionGroupsByType = useMemo(() => {
    const groups: Record<string, { channel: BoardChannel; name: string }[]> = {};
    channels.forEach((channel) => {
      const type = channel.channelType;
      if (!groups[type]) groups[type] = [];
      groups[type].push({ channel, name: channel.name });
    });
    return groups;
  }, [channels]);



  // Modal state management
  const [isChannelEntityModalOpen, setIsChannelEntityModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [isAddChannelModalOpen, setIsAddChannelModalOpen] = useState(false);
  const [isChartsModalOpen, setIsChartsModalOpen] = useState(false);
  const [pendingChartLocation, setPendingChartLocation] = useState<{ x: number; y: number } | null>(null);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [isPageTypeModalOpen, setIsPageTypeModalOpen] = useState(false);
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [isChartsModalHidden, setIsChartsModalHidden] = useState(false);
  const [isBoardSpecModalOpen, setIsBoardSpecModalOpen] = useState(false);
  const [editingSpec, setEditingSpec] = useState<any | null>(null);
  const [activeEntityChannel, setActiveEntityChannel] = useState<Channel | null>(null);
  const [activeEntityRootId, setActiveEntityRootId] = useState<string | undefined>(undefined);
  const [activeEntityJournal, setActiveEntityJournal] = useState<BoardJournal | null>(null);

  // New connection flow modals
  const [isSelectConnectionTypeModalOpen, setIsSelectConnectionTypeModalOpen] = useState(false);
  const [isSelectChannelSourceModalOpen, setIsSelectChannelSourceModalOpen] = useState(false);
  const [isSelectJournalSourceModalOpen, setIsSelectJournalSourceModalOpen] = useState(false);

  // In-board journal details modal + one-time name prompt for auto-created journals
  const [isJournalDetailsModalOpen, setIsJournalDetailsModalOpen] = useState(false);
  const [activeJournalForDetails, setActiveJournalForDetails] = useState<BoardJournal | null>(null);
  const [pendingNameJournal, setPendingNameJournal] = useState<BoardJournal | null>(null);
  const [isCreatingJournal, setIsCreatingJournal] = useState(false);
  const isCreatingJournalRef = useRef(false);
  // Set to true by handleCloseJournalDetailsModal while a background creation is
  // still running — we skip the post-creation rename prompt and modal reopen.
  const journalCreationCancelledRef = useRef(false);
  const [shouldEditBoardName, setShouldEditBoardName] = useState(false);
  const [returnToContentModal, setReturnToContentModal] = useState(false);
  const [channelEntityModalView, setChannelEntityModalView] = useState<'channels' | 'entities' | 'journals' | 'operations'>('channels');
  const [channelEntityModalViewOnly, setChannelEntityModalViewOnly] = useState(false);
  const [featureExplanation, setFeatureExplanation] = useState<{ isOpen: boolean; type: 'connections' | 'specs' }>({ isOpen: false, type: 'connections' });
  const [activeEntityHighlightId, setActiveEntityHighlightId] = useState<string | undefined>(undefined);
  const [shouldHighlightEntity, setShouldHighlightEntity] = useState(false);

  // Pseudo-channel picker (used when adding a Meta/TikTok-style channel — the
  // user picks one ad account at a time, each becomes its own BoardChannel row
  // on the backend).
  const [isPseudoPickerOpen, setIsPseudoPickerOpen] = useState(false);
  const [pseudoPickerChannel, setPseudoPickerChannel] = useState<Channel | null>(null);


  // Plan Feature Gate state
  const [upgradeModal, setUpgradeModal] = useState<{ isOpen: boolean; featureKey: string; currentUsage?: number }>({ isOpen: false, featureKey: '' });
  const [syncErrorModal, setSyncErrorModal] = useState<{
    isOpen: boolean;
    channelName?: string;
    errorMessage?: string;
  }>({ isOpen: false });

  const { fetchFeatures: fetchPlanFeatures, getFeatureValue } = usePlanFeaturesStore();

  // Fetch plan features on mount
  useEffect(() => {
    fetchPlanFeatures();
  }, [fetchPlanFeatures]);

  // Load user channels on mount to ensure store is populated for modals
  useEffect(() => {
    loadUserChannels();
  }, [loadUserChannels]);

  // AI Task banner state
  const [isAITaskBannerOpen, setIsAITaskBannerOpen] = useState(() => {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(`dashbal:ai-banner-open:${id}`) === 'true' : false;
  });
  const [aiPrompt, setAiPrompt] = useState(() => {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(`dashbal:ai-prompt:${id}`) || "" : "";
  });
  const [hasAIInputChanged, setHasAIInputChanged] = useState(false);
  const [aiMode, setAiMode] = useState<'history' | 'conversation'>(() => {
    return typeof localStorage !== 'undefined' ? (localStorage.getItem(`dashbal:ai-mode:${id}`) as 'history' | 'conversation') || 'history' : 'history';
  });
  const [activeTaskId, setActiveTaskId] = useState<string | null>(() => {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(`dashbal:ai-active-task:${id}`) : null;
  });

  // Sync AI states to localStorage
  useEffect(() => {
    if (typeof localStorage !== 'undefined' && id) {
      localStorage.setItem(`dashbal:ai-banner-open:${id}`, isAITaskBannerOpen.toString());
      localStorage.setItem(`dashbal:ai-prompt:${id}`, aiPrompt);
      localStorage.setItem(`dashbal:ai-mode:${id}`, aiMode);
      if (activeTaskId) {
        localStorage.setItem(`dashbal:ai-active-task:${id}`, activeTaskId);
      } else {
        localStorage.removeItem(`dashbal:ai-active-task:${id}`);
      }
    }
  }, [id, isAITaskBannerOpen, aiPrompt, aiMode, activeTaskId]);
  const [isTaskApprovalMode, setIsTaskApprovalMode] = useState(false);
  const [approvalTasks, setApprovalTasks] = useState<{ id: string, text: string, checked: boolean, accepted?: boolean, status?: 'success' | 'error' | 'pending' }[]>([]);
  const [aiTaskConfirmationModal, setAiTaskConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({ isOpen: false, title: "", message: "", onConfirm: () => { } });

  // History pagination
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Messages pagination for active conversation
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesPageByTask, setMessagesPageByTask] = useState<Record<string, number>>({});

  const [previousAITasks, setPreviousAITasks] = useState<any[]>([]);

  // Load conversations from database
  useEffect(() => {
    if (!id) return;
    const fetchConversations = async () => {
      if (loadingHistory || !hasMoreHistory) return;
      setLoadingHistory(true);
      try {
        const response = await aiService.getConversations(id, historyPage, 20);
        if (response.data) {
          const mappedTasks = response.data.map((conv: any) => ({
            id: conv.id,
            prompt: conv.title || "Conversation",
            status: conv.status === 'active' ? 'completed' : conv.status,
            timestamp: conv.createdAt,
            messages: []
          }));

          if (historyPage === 1) {
            setPreviousAITasks(mappedTasks);
          } else {
            setPreviousAITasks(prev => {
              const newTasks = mappedTasks.filter((mT: any) => !prev.some(p => p.id === mT.id));
              return [...prev, ...newTasks];
            });
          }
          setHasMoreHistory(response.pagination?.page < response.pagination?.pages);
        }
      } catch (err) {
        console.error("Failed to load AI conversations", err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchConversations();
  }, [id, historyPage]);

  // Load messages for active conversation
  const fetchMessages = async (taskId: string, page: number) => {
    if (!id || loadingMessages || (!hasMoreMessages && page > 1)) return;
    setLoadingMessages(true);
    try {
      const resp = await aiService.getConversationMessages(id, taskId, page, 20);
      const newMessages = resp.data || [];

      setPreviousAITasks(prev => prev.map(task => {
        if (task.id === taskId) {
          const currentMsgs = task.messages || [];
          const combined = page === 1 ? newMessages : [...newMessages, ...currentMsgs];
          return { ...task, messages: combined };
        }
        return task;
      }));
      setHasMoreMessages(resp.pagination?.page < resp.pagination?.pages);
      setMessagesPageByTask(prev => ({ ...prev, [taskId]: page }));
    } catch (err) {
      console.error("Failed to fetch messages", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (activeTaskId && aiMode === 'conversation') {
      const task = previousAITasks.find(t => t.id === activeTaskId);
      if (task && (!task.messages || task.messages.length === 0)) {
        setHasMoreMessages(true);
        fetchMessages(activeTaskId, 1);
      }
    }
  }, [activeTaskId, aiMode, previousAITasks]);

  // Derive current conversation
  const currentConversation: any[] = previousAITasks.find((t: any) => t.id === activeTaskId)?.messages || [];


  // Constants


  // AI Mode reset logic removed to allow state persistence when using spacebar shortcut
  // as per requirement "ai banner shows on the mode it was"

  // Synchronize AI Banner state with browser history for back button support
  const bannerStateRef = useRef({
    isOpen: isAITaskBannerOpen,
    mode: aiMode,
    isTask: isTaskApprovalMode
  });
  const isHandlingPopRef = useRef(false);

  useEffect(() => {
    const handlePopState = () => {
      const current = bannerStateRef.current;
      if (!current.isOpen) return;

      isHandlingPopRef.current = true;
      if (current.isTask) {
        setIsTaskApprovalMode(false);
        // Re-push to keep interception alive for next back
        window.history.pushState({ aiBanner: true }, '');
      } else if (current.mode === 'conversation') {
        setAiMode('history');
        // Re-push to keep interception alive for next back
        window.history.pushState({ aiBanner: true }, '');
      } else {
        // In history mode, close banner
        setIsAITaskBannerOpen(false);
      }
      // isHandlingPopRef is reset in the next effect after state updates
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Only register once

  useEffect(() => {
    const prev = bannerStateRef.current;

    // When banner opens via UI
    if (isAITaskBannerOpen && !prev.isOpen && !isHandlingPopRef.current) {
      window.history.pushState({ aiBanner: true }, '');
    }
    // When banner closes via UI
    if (!isAITaskBannerOpen && prev.isOpen && !isHandlingPopRef.current) {
      if (window.history.state?.aiBanner) {
        window.history.back();
      }
    }

    bannerStateRef.current = { isOpen: isAITaskBannerOpen, mode: aiMode, isTask: isTaskApprovalMode };
    isHandlingPopRef.current = false;
  }, [isAITaskBannerOpen, aiMode, isTaskApprovalMode]);

  // Reset AI input change tracker when banner closes
  useEffect(() => {
    if (!isAITaskBannerOpen) {
      setHasAIInputChanged(false);
    }
  }, [isAITaskBannerOpen]);

  // Listen for AI trigger events from components (like AIVerdictModule)
  useEffect(() => {
    const handleTriggerAI = (e: any) => {
      const { prompt, context } = e.detail;
      const verdict = context?.verdict;
      
      const initialHistory = [];
      if (verdict) {
          // Format the structured verdict to Markdown for the AI chat context
          let md = `# Current strategic Verdict: ${verdict.title || 'Analysis'}\n\n`;
          md += `${verdict.message || ''}\n\n`;
          if (verdict.recommendation) {
              md += `**Top Recommendation:** ${verdict.recommendation}\n\n`;
          }
          if (verdict.details?.length > 0) {
              md += `### Strategic Assessment:\n`;
              verdict.details.forEach((d: string) => { md += `- ${d}\n`; });
              md += `\n`;
          }
          if (verdict.metrics?.length > 0) {
              md += `### Key Performance metrics relevant to this context :\n`;
              verdict.metrics.forEach((m: any) => {
                  md += `- **${m.label}**: ${m.value}`;
                  if (m.trend) md += ` (${m.trend === 'up' ? '↑' : m.trend === 'down' ? '↓' : '→'})`;
                  md += `\n`;
              });
          }
          initialHistory.push({ role: 'assistant', content: md });
      }

      setAiMode('conversation');
      setIsAITaskBannerOpen(true);
      
      // Auto-send the task with history
      handleSendAITask(prompt, initialHistory);
    };

    window.addEventListener('dashbal:trigger-ai', handleTriggerAI);
    return () => window.removeEventListener('dashbal:trigger-ai', handleTriggerAI);
  }, []);

  // AI execution logic is now handled directly by handleSendAITask and handleValidateTasks
  useEffect(() => {
    setHasAITasks(previousAITasks.length > 0);
  }, [previousAITasks.length, setHasAITasks]);


  // Ref to track if charts modal should reopen after action (drag/click)
  const shouldReopenChartsModalRef = useRef(false);
  const aiTextAreaRef = useRef<HTMLTextAreaElement>(null);

  // AI Prompt auto-resize
  useEffect(() => {
    if (aiTextAreaRef.current) {
      // Temporarily set height to a small value to accurately measure scrollHeight
      // Using 20px as it matches our text-[15px] leading-[20px]
      aiTextAreaRef.current.style.height = '20px';
      
      const maxHeight = window.innerHeight * 0.4;
      const scrollHeight = aiTextAreaRef.current.scrollHeight;
      
      const targetHeight = Math.max(20, Math.min(scrollHeight, maxHeight));
      aiTextAreaRef.current.style.height = `${targetHeight}px`;
      
      // Explicitly hide scrollbars unless we've actually hit the max height
      aiTextAreaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      aiTextAreaRef.current.style.overflowX = 'hidden';
    }
  }, [aiPrompt]);

  const titleButtonRef = useRef<HTMLDivElement>(null);
  const [showDateRange, setShowDateRange] = useState(true);
  const [showNetValue, setShowNetValue] = useState(true);
  const [collapseConnections, setCollapseConnections] = useState(false);
  const [showAITriggerArea, setShowAITriggerArea] = useState(true);



  useEffect(() => {
    if (!titleButtonRef.current) return;
    
    let rafId: number;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const width = entries[0].contentRect.width;
        
        if (width < 250) {
          setCollapseConnections(true);
          setShowDateRange(false);
          setShowNetValue(width > 180);
          setShowAITriggerArea(false);
        } else if (width < 380) {
          setCollapseConnections(true);
          setShowDateRange(false);
          setShowNetValue(true);
          setShowAITriggerArea(false);
        } else if (width < 520) {
          setCollapseConnections(true);
          setShowDateRange(true);
          setShowNetValue(true);
          setShowAITriggerArea(false);
        } else if (width < 600) {
          setCollapseConnections(false);
          setShowDateRange(true);
          setShowNetValue(true);
          setShowAITriggerArea(false);
        } else if (width < 680) {
          setCollapseConnections(false);
          setShowDateRange(true);
          setShowNetValue(true);
          setShowAITriggerArea(true);
        } else {
          setCollapseConnections(false);
          setShowDateRange(true);
          setShowNetValue(true);
          setShowAITriggerArea(true);
        }
      });
    });

    observer.observe(titleButtonRef.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Format dynamic content for header
  const getHeaderCurrencySymbol = (code: string) => {
    const symbols: Record<string, string> = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$',
        'CHF': 'Fr', 'CNY': '¥', 'SEK': 'kr', 'NZD': 'NZ$', 'TND': 'DT',
    };
    return code ? (symbols[code] || code) : '$';
  };

  const formatHeaderCompactNumber = (num: number, currencyCode: string = '') => {
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    const symbol = getHeaderCurrencySymbol(currencyCode);
    
    if (absNum >= 1000000) {
        return `${sign}${symbol}${(absNum / 1000000).toFixed(1)}M`;
    } else if (absNum >= 1000) {
        return `${sign}${symbol}${(absNum / 1000).toFixed(1)}K`;
    }
    return `${sign}${symbol}${absNum.toFixed(0)}`;
  };
  // Ensure AI text field is focused with cursor at the end when banner opens or mode changes
  useEffect(() => {
    if (isAITaskBannerOpen && !isTaskApprovalMode && aiTextAreaRef.current) {
      const focusTextarea = () => {
        const el = aiTextAreaRef.current;
        if (el) {
          const length = el.value.length;
          el.focus();
          // Force cursor to the end of existing text
          el.setSelectionRange(length, length);
        }
      };
      
      // Attempt focus immediately
      focusTextarea();

      // Defer slightly to account for framer-motion animations
      const timer = setTimeout(focusTextarea, 50);
      return () => clearTimeout(timer);
    }
  }, [isAITaskBannerOpen, aiMode, isTaskApprovalMode]);


  const [workingChannels, setWorkingChannels] = useState<Channel[]>([]);
  const [workingSelectedEntityIds, setWorkingSelectedEntityIds] = useState<string[]>([]);
  const [workingSelectedOperationIds, setWorkingSelectedOperationIds] = useState<string[]>([]);
  const [initialSelectedOperationIds, setInitialSelectedOperationIds] = useState<Set<string>>(new Set());

  // State to track initial values for comparison
  const [initialChannelIds, setInitialChannelIds] = useState<Set<string>>(new Set());
  const [initialSelectedEntityIds, setInitialSelectedEntityIds] = useState<Set<string>>(new Set());

  // Confirmation Modal state moved higher to avoid use-before-declaration
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
    variant?: "primary" | "danger" | "warning";
  }>({ isOpen: false, title: "", message: "", onConfirm: () => { } });





  const { user } = useAuthStore();
  // Derived currency state
  const boardCurrency = currencySettings?.default || user?.currency || "";
  const currencyConversions = currencySettings?.conversions || [];



  // Handle currency save
  const handleCurrencySave = async (
    newDefaultCurrency: string,
    conversions: Array<{ currency: string; rate: number; manual: boolean }>
  ) => {
    if (!id) return;

    setIsDataLoading(true);
    try {
      const currencyChanged = newDefaultCurrency !== boardCurrency;
      const ratesChanged = JSON.stringify(conversions) !== JSON.stringify(currencyConversions);

      if (!currencyChanged && !ratesChanged) return;

      const exchangeRates: { [key: string]: { rate: number; manual: boolean } } = {};
      conversions.forEach((conv) => {
        exchangeRates[conv.currency] = {
          rate: conv.rate,
          manual: conv.manual,
        };
      });

      await boardService.updateBoard(id, {
        currency: newDefaultCurrency,
        exchangeRates,
      });

      setCurrencySettings({
        default: newDefaultCurrency,
        conversions,
      });

      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
    } catch (error: any) {
      console.error("Error saving currency settings:", error);
      throw error;
    } finally {
      setIsDataLoading(false);
    }
  };

  // Handle board name update
  const handleBoardNameUpdate = async (newName: string) => {
    if (!id || !newName.trim()) return;

    setIsDataLoading(true);
    try {
      await boardService.updateBoard(id, {
        name: newName.trim(),
      });

      // Invalidate queries to refresh board data
      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
    } catch (error: any) {
      console.error('Error updating board name:', error);
      showAlert({
        type: 'error',
        title: t('errors.error') as string,
        message: t('pages.unableToRename') as string,
      });
    } finally {
      setIsDataLoading(false);
    }
  };
  const prevPageIdRef = useRef<string | null>(activePageId);

  useEffect(() => {
    prevPageIdRef.current = activePageId;
  }, [activePageId]);

  // Auto-open content modal when new channel errors occur
  const prevErrorKeysRef = useRef<string>("");
  useEffect(() => {
    const currentErrorKeys = Object.keys(channelErrors || {}).sort().join(',');
    // If we have errors and the set of errored channels has changed (e.g. new ones appeared)
    // we open the modal to alert the user.
    if (currentErrorKeys && currentErrorKeys !== prevErrorKeysRef.current) {
      setIsContentModalOpen(true);
    }
    prevErrorKeysRef.current = currentErrorKeys;
  }, [channelErrors]);

  // Animation State Calculation
  // We calculate this on every render based on the current relation between activePageId and prevPageIdRef
  const getAnimationProps = () => {
    const prevId = prevPageIdRef.current;
    const currId = activePageId;

    // Default transition if IDs are same (stable state)
    // We use initial: false to prevent re-triggering fade-in on every re-render
    if (prevId === currId) {
      return {
        initial: false,
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.2 }
      };
    }

    // Page to Page Transition: simple fade
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2, ease: "easeOut" as const }
    };
  };

  const animationProps = getAnimationProps();

  // Fetch full board data (syncs to store automatically)
  // The board constant is already declared above using useBoardStore
  // and boardFullResponse is also declared above. Removing these duplicates.

  // Enable WebSocket for real-time updates
  useBoardWebSocket(id || null);

  useEffect(() => {
    try {
      const ws = getWebSocketClient();
      const handleAIEvent = (data: any) => {
        if (data.boardId !== id) return;
        console.log('[AI Event Received]', data);

        // Check if we have this conversation in our history
        const isKnownConversation = previousAITasks.some((t: any) => t.id === data.conversationId);

        if (isKnownConversation) {
          handleAIFollowUp(data.conversationId, data.eventType, data.metadata);
        }
      };

      const handleAIProgress = (data: any) => {
        if (data.boardId !== id) return;
        // Removed addPersistentNotice call and msg variable usage
      };

      const handleAIComplete = async (data: any) => {
        if (data.boardId !== id) return;

        // Handle navigation if a page was created
        if (data.results) {
          const createPageResult = data.results.find((r: any) => r.result?.pageId)?.result;
          if (createPageResult?.pageId) {
            handleSelectPage(createPageResult.pageId);
          }
        }

        // Invalidate queries if there's any
        if (data.invalidateQueries && Array.isArray(data.invalidateQueries) && data.invalidateQueries.length > 0) {
          await Promise.all(data.invalidateQueries.map(async (queryKey: string) => {
            await queryClient.invalidateQueries({ queryKey: [queryKey, id] });
            // Force a refetch for the main board payload so the store is rebuilt
            // synchronously before any follow-up UI (e.g. opening the spec modal
            // from a notice click) reads `specs` / `pages` from the store.
            if (queryKey === 'board-payload') {
              await queryClient.refetchQueries({ queryKey: [queryKey, id] });
            }
          }));
        }

        const summaryMsg = data.summary || (data.error
          ? t("boards.ai.status.executionFailed", "Failed to execute plan.")
          : t("boards.ai.status.executionComplete", "Execution complete."));

        setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
          if (task.id === data.conversationId) {
            const newMessages = [...(task.messages || [])];
            const lastMsgAi = newMessages[newMessages.length - 1];
            if (lastMsgAi && lastMsgAi.role === 'assistant') {
              newMessages[newMessages.length - 1] = {
                ...lastMsgAi,
                content: summaryMsg
              };
            }
            return { ...task, status: data.error ? 'error' : 'completed', messages: newMessages };
          }
          return task;
        }));
      };

      const handleChannelSynced = async (data: any) => {
        const { channelId } = data;
        
        // 1. Let the event system handle notice updates/dismissals
        
        // 2. Update local working set for the synced channel
        const channelInWorkingSet = workingChannels.find(c => String(c.id) === String(channelId));
        if (channelInWorkingSet) {
          try {
            const response = await channelsService.getEntities(channelId);
            if (response.success && response.data) {
                setWorkingChannels((prev) => prev.map((c) =>
                  String(c.id) === String(channelId) ? { ...c, status: "active", isLoadingEntities: false, entities: response.data, elements: response.data } : c
                ));
                if (String(activeEntityChannel?.id) === String(channelId)) {
                  setActiveEntityChannel((prev) => prev ? { ...prev, status: "active", isLoadingEntities: false, entities: response.data, elements: response.data } : null);
                }
            }
          } catch (error) {
            console.error("Error fetching synced channel entities:", error);
          }
        }

        // 3. Update global channel stores and invalidations
        updateUserChannel(channelId, { status: 'active' as const });
        loadUserChannels(true);
        queryClient.invalidateQueries({ queryKey: ["board-payload"] });
        
        // 4. Show the specific "Sync Finished" notice
        // 4. Show the specific "Sync Finished" notice
        
        // Removed addPersistentNotice call
      };

      ws.on('ai:event_triggered', handleAIEvent);
      ws.on('ai:task_progress', handleAIProgress);
      ws.on('ai:task_complete', handleAIComplete);
      ws.on('channel:synced', handleChannelSynced);

      return () => {
        ws.off('ai:event_triggered', handleAIEvent);
        ws.off('ai:task_progress', handleAIProgress);
        ws.off('ai:task_complete', handleAIComplete);
        ws.off('channel:synced', handleChannelSynced);
      };
    } catch (e) {
      // getWebSocketClient might throw if not initialized yet
    }
  }, [id, previousAITasks, workingChannels, activeEntityChannel, updateUserChannel, loadUserChannels, queryClient]);

  // Use custom hook for date range updates
  const { handleDateRangeChange, dateRangeUpgrade, closeDateRangeUpgrade } = useDateRange({ boardId: id });

  // Load user channels store - this caches channel data for modals
  const { 
    channels: userChannels, 
    isLoaded: userChannelsLoaded, 
    isLoading: userChannelsLoading 
  } = useUserChannelsStore();

  // Load user channels when board page opens (force reload if returning from a new connection flow)
  useEffect(() => {
    const hasNewChannel = searchParams.get('newChannelId') !== null;
    loadUserChannels(hasNewChannel);
  }, [loadUserChannels, searchParams]);

  const handleDeleteSpec = async (specId: string) => {
    if (!id) return;
    try {
      // Update store immediately for instant UI feedback
      const updatedSpecs = specs.filter(s => s.id !== specId);
      setSpecs(updatedSpecs);

      // Perform deletion on server
      await boardService.deleteBoardSpec(id, specId);

      // Optimistically update local store
      setSpecs(specs.filter(s => s.id !== specId));

      // Invalidate queries to refresh calculated values in background
      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
} catch (error) {
      console.error('Failed to delete spec', error);
      // Revert store state if deletion fails (optional but good)
      // For now, reload specs from server if there's an error
      const currentSpecs = await boardService.getBoardSpecs(id);
      setSpecs(currentSpecs.data);

      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete spec'
      });
    }
  };

  const handleStopTask = (taskId: string) => {
    setPreviousAITasks(prev => prev.map(task => {
      if (task.id === taskId) {
        const newMessages = [...(task.messages || [])];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
          newMessages[newMessages.length - 1] = {
            ...lastMsg,
            content: t("boards.ai.status.interrupted")
          };
        }
        return { ...task, status: 'cancelled', messages: newMessages };
      }
      return task;
    }));
  };

  const handleDeleteTask = (task: any) => {
    setAiTaskConfirmationModal({
      isOpen: true,
      title: t("boards.ai.confirmDeleteTitle") as string,
      message: t("boards.ai.confirmDeleteMessage", { prompt: task.prompt }) as string,
      confirmText: t("actions.delete") as string,
      cancelText: t("actions.cancel") as string,
      onConfirm: async () => {
        try {
          if (id) {
            await aiService.deleteConversation(id, task.id);
          }
          setPreviousAITasks(prev => prev.filter(item => item.id !== task.id));
        } catch (error) {
          console.error("Failed to delete conversation", error);
        } finally {
          setAiTaskConfirmationModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleValidateTasks = async () => {
    setIsTaskApprovalMode(false);

    if (!activeTaskId || !id) return;

    // Only pending (non-accepted) tasks should be approved/executed;
    // auto-executed tasks are display-only.
    const pendingTasks = approvalTasks.filter((t: any) => !t.accepted);
    const approvedTasks = pendingTasks.filter((t: any) => t.checked);
    const approvedCount = approvedTasks.length;

    const currentTask = previousAITasks.find((t: any) => t.id === activeTaskId);
    const lastMsg = currentTask?.messages?.[currentTask.messages.length - 1];
    const planId = lastMsg?.planId;

    const msgId = Date.now().toString();

    // Close banner and show feedback when task starts
    setIsAITaskBannerOpen(false);
    setIsStackedNoticesOpen(true);

    // Append only an empty assistant placeholder — the loader renders here
    // and gets replaced with the summary on `ai:task_complete`. The user's
    // approval is implicit from the "Validate & Execute" button click; we
    // don't echo a synthetic user message into the chat.
    setPreviousAITasks((prev: any[]) => prev.map(task => {
      if (task.id === activeTaskId) {
        return {
          ...task,
          status: 'loading',
          messages: [
            ...(task.messages || []),
            { id: msgId + '-ai', role: 'assistant', content: '' }
          ]
        }
      }
      return task;
    }));

    if (approvedCount > 0 && planId) {
      try {
        const enabledActionIds = approvedTasks.map((t: any) => t.id);
        // Removed addPersistentNotice call
        await aiService.confirmPlan(id as string, planId, enabledActionIds);

        // Let the webhooks ('ai:task_progress' and 'ai:task_complete') handle the rest.
        // We leave the task in 'loading' status until 'ai:task_complete' arrives.

      } catch (e) {
        setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
          if (task.id === activeTaskId) {
            const newMessages = [...(task.messages || [])];
            const lastMsgAi = newMessages[newMessages.length - 1];
            newMessages[newMessages.length - 1] = {
              ...lastMsgAi,
              content: t("boards.ai.status.executionFailed", "Failed to execute plan.")
            };
            return { ...task, status: 'error', messages: newMessages };
          }
          return task;
        }));
      }
    } else {
      // if no planId or approvedCount === 0, just leave as completed
      setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
        if (task.id === activeTaskId) {
          const newMessages = [...(task.messages || [])];
          const lastMsgAi = newMessages[newMessages.length - 1];
          newMessages[newMessages.length - 1] = {
            ...lastMsgAi,
            content: t("boards.ai.status.executionCancelled", "I will not execute these tasks.")
          };
          return { ...task, status: 'completed', messages: newMessages };
        }
        return task;
      }));
    }
  };

  async function handleAIFollowUp(conversationId: string, eventType: string, metadata?: any) {
    if (!id) return;

    // Context-aware follow-up messages
    let followUpPrompt = "";
    let shouldOpenBanner = true;

    switch (eventType) {
      case 'CHANNEL_CONNECTED':
        followUpPrompt = "The user has successfully connected a new channel. Please finish your analysis and suggest next steps based on this new source.";
        break;
      case 'JOURNAL_CREATED':
        followUpPrompt = "The user has created a new money journal. Please update your analysis to include these manual expenses/revenues.";
        break;
      case 'SPEC_CREATED':
        followUpPrompt = "The user has created a new board spec (custom formula). Please analyze how this affects the business overview.";
        break;
      case 'PAGE_CREATED':
        shouldOpenBanner = false;
        followUpPrompt = "The user has created a new page. Please suggest what analysis would be most valuable there.";
        break;
      case 'DATE_RANGE_UPDATED':
        followUpPrompt = "The user has updated the date range. Please re-evaluate performance trends for this new period.";
        break;
      case 'CURRENCY_UPDATED':
        followUpPrompt = "The user has updated the board currency. Please ensure all previous analyses remain consistent.";
        break;
      case 'ENTITIES_SELECTED':
        followUpPrompt = "The user has updated the entity selection. Please adjust your analysis to reflect the new data scope.";
        break;
      case 'OPERATIONS_CREATED':
        followUpPrompt = "The user has added manual transactions. Please factor these into the final analysis.";
        break;
      default:
        followUpPrompt = `Action completed: ${eventType}. Please continue your analysis and provide final recommendations.`;
    }

    // Set loading state for this conversation
    setPreviousAITasks(prev => prev.map(task => {
      if (task.id === conversationId) {
        return {
          ...task,
          status: 'loading',
          messages: [
            ...(task.messages || []),
            { id: Date.now().toString() + '-ai', role: 'assistant', content: '' }
          ]
        };
      }
      return task;
    }));

    try {
      const currentTask = previousAITasks.find(t => t.id === conversationId);
      const history = (currentTask?.messages || []).map((m: any) => ({ role: m.role, content: m.content }));

      // Include metadata in follow-up prompt if available
      const enrichedPrompt = metadata ? `${followUpPrompt} (Context: ${JSON.stringify(metadata)})` : followUpPrompt;

      if (shouldOpenBanner) {
        setIsAITaskBannerOpen(true);
      }
      setAiMode('conversation');
      setActiveTaskId(conversationId);

      const uiContext = {
        activePageId,
        activePageName: !activePageId || activePageId === 'MAIN' ? 'Main Board' : pages.find((p: any) => p.id === activePageId)?.name || 'Unknown Page',
        availablePages: pages.map((p: any) => ({ id: p.id, name: p.name, type: p.type }))
      };

      const plan = await aiService.sendPrompt(id as string, enrichedPrompt, history, conversationId, uiContext);

      const acceptedTasksFu = (plan.autoExecutedActions || []).map((a: any) => ({
        id: a.id,
        text: a.description,
        checked: true,
        accepted: true,
        status: a.status || 'success',
      }));
      const pendingTasksFu = (plan.actions || []).map((a: any) => ({
        id: a.id,
        text: a.description,
        checked: true,
        accepted: false,
      }));
      const combinedTasksFu = [...acceptedTasksFu, ...pendingTasksFu];
      const hasAnyTasksFu = combinedTasksFu.length > 0;
      const hasPendingTasksFu = pendingTasksFu.length > 0;

      setPreviousAITasks(prev => prev.map(task => {
        if (task.id === conversationId) {
          const newMessages = [...(task.messages || [])];
          const lastIdx = newMessages.length - 1;
          if (newMessages[lastIdx].role === 'assistant') {
            newMessages[lastIdx] = {
              ...newMessages[lastIdx],
              content: plan.message || (plan.actions
                ? t("boards.ai.responses.followUpActions", "I've prepared some further actions based on this update.")
                : t("boards.ai.responses.followUpAnalysis", "I've updated my analysis.")),
              planId: hasPendingTasksFu ? plan.planId : undefined,
              isTaskApproval: hasAnyTasksFu || undefined,
              approvalTasks: hasAnyTasksFu ? combinedTasksFu : undefined,
            };
          }

          if (hasAnyTasksFu && activeTaskId === conversationId) {
            setApprovalTasks(combinedTasksFu);
            setIsTaskApprovalMode(true);
          }

          // Handle auto-execution invalidations in follow-ups
          if (plan.invalidateQueries && plan.invalidateQueries.length > 0) {
            Promise.all(plan.invalidateQueries.map(async (queryKey: string) => {
              await queryClient.invalidateQueries({ queryKey: [queryKey, id] });
              if (queryKey === 'board-payload') {
                await queryClient.refetchQueries({ queryKey: [queryKey, id] });
              }
            })).catch(err => console.error("Follow-up invalidation failed", err));
          }

          return { ...task, status: 'completed', messages: newMessages };
        }
        return task;
      }));
    } catch (err: any) {
      console.error("AI automated follow-up failed:", err);
      setPreviousAITasks(prev => prev.map(task => {
        if (task.id === conversationId) {
          const newMessages = [...(task.messages || [])];
          if (newMessages.length > 0) {
            const lastMsg = newMessages[newMessages.length - 1];
            if (err?.response?.status === 429 && err?.response?.data?.code === 'AI_QUOTA_EXCEEDED') {
              const quota = err.response.data.quota;
              const resetTime = quota?.resetsAt ? new Date(quota.resetsAt).toLocaleString(undefined, {
                weekday: 'short', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit'
              }) : 'shortly';

              newMessages[newMessages.length - 1] = {
                ...lastMsg,
                content: t("boards.ai.quotaExceeded", { resetTime }),
                isQuotaError: true
              };
            } else {
              newMessages[newMessages.length - 1] = {
                ...lastMsg,
                content: AI_ERROR_SENTINEL,
              };
            }
          }
          return { ...task, status: 'error', messages: newMessages };
        }
        return task;
      }));
    }
  };

  const handleSendAITask = async (
    overriddenPrompt?: string,
    overriddenHistory?: any[],
    options?: { isRetry?: boolean }
  ) => {
    setIsTaskApprovalMode(false);
    const promptValue = overriddenPrompt || aiPrompt.trim();
    if (!promptValue || !id) return;

    const isRetry = options?.isRetry === true;
    const userPrompt = promptValue;
    setAiPrompt("");

    let taskId = activeTaskId;
    let history: any[] = overriddenHistory || [];
    let existingConversationId: string | undefined;

    if (aiMode === 'history' || overriddenHistory) {
      taskId = Date.now().toString();
      const initialMessages = [];
      
      // Inject history if provided (e.g. AI verdict context)
      if (overriddenHistory) {
          overriddenHistory.forEach(h => initialMessages.push({ ...h, id: Date.now().toString() + '-' + Math.random() }));
      }
      
      initialMessages.push({ id: Date.now().toString() + '-user', role: 'user', content: userPrompt });
      initialMessages.push({ id: Date.now().toString() + '-ai', role: 'assistant', content: '' });

      const newTask = {
        id: taskId,
        prompt: userPrompt,
        status: 'loading',
        timestamp: new Date().toISOString(),
        messages: initialMessages
      };

      setAiMode('conversation');
      setActiveTaskId(taskId);
      setPreviousAITasks((prev: any[]) => [newTask, ...prev]);
    } else {
      if (taskId) {
        const existingTask = previousAITasks.find((t: any) => t.id === taskId);
        existingConversationId = existingTask?.conversationId;
        const existingMsgs = existingTask?.messages || [];

        // Build LLM history WITHOUT trailing error sentinels — the backend
        // deletes them from the DB on the next prompt and the LLM should
        // never see them. But we keep the sentinel in the UI messages so
        // the error text stays visible (only its retry button disappears
        // because it won't be the last message any more).
        const msgsForHistory = [...existingMsgs];
        while (
          msgsForHistory.length > 0 &&
          msgsForHistory[msgsForHistory.length - 1].role === 'assistant' &&
          msgsForHistory[msgsForHistory.length - 1].content === AI_ERROR_SENTINEL
        ) {
          msgsForHistory.pop();
        }

        history = msgsForHistory.map((m: any) => ({
          role: m.role,
          content: m.content,
        }));

        const msgId = Date.now().toString();
        const lastForHistory = msgsForHistory[msgsForHistory.length - 1];
        const replayLastUser =
          isRetry &&
          lastForHistory?.role === 'user' &&
          lastForHistory?.content === userPrompt;

        // Keep the full existingMsgs (including error sentinel) for display,
        // then append the new user message + loading placeholder.
        setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
          if (task.id === taskId) {
            const nextMessages = replayLastUser
              ? [...existingMsgs, { id: msgId + '-ai', role: 'assistant', content: '' }]
              : [
                  ...existingMsgs,
                  { id: msgId + '-user', role: 'user', content: userPrompt },
                  { id: msgId + '-ai', role: 'assistant', content: '' },
                ];
            return { ...task, status: 'loading', messages: nextMessages };
          }
          return task;
        }));
      }
    }

    try {
      const uiContext = {
        activePageId,
        activePageName: !activePageId || activePageId === 'MAIN' ? 'Main Board' : pages.find((p: any) => p.id === activePageId)?.name || 'Unknown Page',
        availablePages: pages.map((p: any) => ({ id: p.id, name: p.name, type: p.type }))
      };

      const plan = await aiService.sendPrompt(id as string, userPrompt, history, existingConversationId, uiContext, isRetry);

      // Handle UI instructions and auto-execution feedback
      if (plan.readResults) {
        plan.readResults.forEach((r: any) => {
          if (r.result?.openModal) {
            setIsAITaskBannerOpen(false);
            switch (r.result.openModal) {
              case 'add_connection':
                setIsSelectConnectionTypeModalOpen(true);
                break;
              case 'add_chart':
                handleOpenChartsModal();
                break;
              case 'page_templates':
                setIsTemplatesModalOpen(true);
                break;
              case 'board_settings':
                setIsContentModalOpen(true);
                break;
              case 'currency_settings':
                setIsCurrencyModalOpen(true);
                break;
            }
          }
        });
      }

      // Handle auto-execution invalidations
      if (plan.invalidateQueries && plan.invalidateQueries.length > 0) {
        setIsAITaskBannerOpen(false);
        setIsStackedNoticesOpen(true);
        await Promise.all(plan.invalidateQueries.map(async (queryKey: string) => {
          await queryClient.invalidateQueries({ queryKey: [queryKey, id] });
          if (queryKey === 'board-payload') {
            await queryClient.refetchQueries({ queryKey: [queryKey, id] });
          }
        }));
      }

      if (plan.conversationId && plan.conversationId !== taskId) {
        setActiveTaskId(plan.conversationId);
      }

      const acceptedTasks = (plan.autoExecutedActions || []).map((a: any) => ({
        id: a.id,
        text: a.description,
        checked: true,
        accepted: true,
        status: a.status || 'success',
      }));
      const pendingTasks = (plan.actions || []).map((a: any) => ({
        id: a.id,
        text: a.description,
        checked: true,
        accepted: false,
      }));
      const combinedTasks = [...acceptedTasks, ...pendingTasks];
      const hasAnyTasks = combinedTasks.length > 0;
      const hasPendingTasks = pendingTasks.length > 0;

      setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
        if (task.id === taskId) {
          const newMessages = [...(task.messages || [])];
          const lastMsg = newMessages[newMessages.length - 1];

          if (hasAnyTasks) {
            newMessages[newMessages.length - 1] = {
              ...lastMsg,
              isTaskApproval: true,
              planId: hasPendingTasks ? plan.planId : undefined,
              approvalTasks: combinedTasks,
              content: plan.message || (hasPendingTasks
                ? t("boards.ai.mock.approvalMessage")
                : t("boards.ai.acceptedTasksMessage", "I've completed the following actions on your behalf."))
            };
          } else {
            newMessages[newMessages.length - 1] = {
              ...lastMsg,
              content: plan.message || "I've analyzed your request."
            };
          }

          return { ...task, id: plan.conversationId || task.id, status: 'completed', conversationId: plan.conversationId, messages: newMessages };
        }
        return task;
      }));

      // Auto-open the task view for the active conversation so the user can
      // see what was just accepted/auto-executed (and review pending items).
      if (hasAnyTasks) {
        setApprovalTasks(combinedTasks);
        setIsTaskApprovalMode(true);
      }

    } catch (err: any) {
      // Backend echoes the in-progress conversationId on errors so the next
      // retry can resume the same conversation. Without this the retry sends
      // conversationId: undefined and the backend forks a brand-new one.
      const errConversationId = err?.response?.data?.conversationId;
      setPreviousAITasks((prev: any[]) => prev.map((task: any) => {
        if (task.id === taskId) {
          const newMessages = [...(task.messages || [])];
          const lastMsg = newMessages[newMessages.length - 1];

          if (err?.response?.status === 429 && err?.response?.data?.code === 'AI_QUOTA_EXCEEDED') {
            const quota = err.response.data.quota;
            const resetTime = quota?.resetsAt ? new Date(quota.resetsAt).toLocaleString(undefined, {
              weekday: 'short', month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit'
            }) : 'shortly';

            newMessages[newMessages.length - 1] = {
              ...lastMsg,
              content: t("boards.ai.quotaExceeded", { resetTime }),
              isQuotaError: true
            };
          } else {
            // Sentinel — render layer maps it to the error+retry UI. Backend
            // also persisted this same sentinel so a refresh shows the same.
            newMessages[newMessages.length - 1] = {
              ...lastMsg,
              content: AI_ERROR_SENTINEL,
            };
          }

          return {
            ...task,
            id: errConversationId || task.id,
            conversationId: errConversationId || task.conversationId,
            status: 'error',
            messages: newMessages,
          };
        }
        return task;
      }));

      if (errConversationId && errConversationId !== taskId) {
        setActiveTaskId(errConversationId);
      }
    }
  };

  // Listen for channel sync completion to update entities in real-time


  // Initialize working state when entering edit mode (opening any modal)
  // This logic ensures we


  // Derived state for modals
  const isAnyModalOpen =
    isChannelEntityModalOpen ||
    isCurrencyModalOpen ||
    isChannelEntityModalOpen ||
    isAddChannelModalOpen;

  useEffect(() => {
    if (isAnyModalOpen && workingChannels.length === 0 && channels.length > 0) {
      // Initialize with store data if working state is empty
      // We need to merge entities from store into the channel objects
      const channelsWithEntities = channels.map((channel) => {
        // Find entities for this channel from the store's entities list
        // Note: The store might have a flat list of entities in `state.entities`
        // We need to access the store state to get entities
        const storeEntities = useBoardStore.getState().entities;
        const channelEntities = storeEntities.filter(
          (e) => e.channelId === channel.id
        );

        return {
          ...channel,
          entities: channelEntities,
          elements: channelEntities, // For compatibility
        };
      });

      setWorkingChannels(channelsWithEntities as any[]);
      setWorkingSelectedEntityIds(selectedEntities.map((e) => e.id));
    } else if (!isAnyModalOpen && workingChannels.length > 0) {
      // Clear working state when all modals are closed (optional, but good for cleanup)
      // We might want to keep it if we want to persist state between re-opens without saving
      // But for now, let's clear it to ensure fresh start next time unless we want persistence
      // The user requirement says "after that it's behavior is completely independent",
      // which implies we should NOT reset it if we just close and reopen?
      // Actually, "when it's open the default state is to show the channels... after that it's behavior is completely independent"
      // This usually means independent *while* the flow is active. If I close everything, I probably expect a reset.
      setWorkingChannels([]);
      setWorkingSelectedEntityIds([]);
      setActiveEntityChannel(null); // Clear active channel too
    }
  }, [
    isChannelEntityModalOpen,
    isChannelEntityModalOpen,
    isAddChannelModalOpen,
    channels,
    journals,
    selectedEntities,
    isAnyModalOpen,
  ]);

  // Track when validation was just completed to prevent auto-reopening modal
  const justValidatedRef = useRef(false);

  // Check URL params for modal opening instructions
  useEffect(() => {
    // If we just validated, ignore params to prevent re-opening modal during sync/update
    if (justValidatedRef.current) {
      justValidatedRef.current = false;
      return;
    }

    const openModal = searchParams.get('openModal');
    const openContent = searchParams.get('openContent');
    const editTitle = searchParams.get('editTitle');
    const tab = searchParams.get('tab');
    const channelId = searchParams.get('channelId');
    const entityId = searchParams.get('entityId');
    const highlight = searchParams.get('highlight');

    if (openModal || openContent || editTitle || (tab === 'channels' && channelId)) {
      if (editTitle === 'true') {
        setShouldEditBoardName(true);
      }

      // Determine which modal to open
      if (openModal === 'select_channel_source') {
        const newChannelId = searchParams.get('newChannelId');
        // Removed isBridge and added check logic

        if (newChannelId) {
          setIsSelectChannelSourceModalOpen(false);
          // If we have a new channel ID, we MUST wait for a fresh load of user channels.
          // Since we trigger loadUserChannels(true) on mount when this param is present,
          // we should wait until userChannelsLoading starts and then finishes.
          // A simple way is to wait for userChannelsLoaded to be true, but if it was already true 
          // from a previous visit, we need to be sure we're not looking at stale data.
          
          if (userChannelsLoading) return; // Wait for loading to finish
          
          const channel = userChannels.find(c => c.id === newChannelId);
          
          // If channel is not found, it might be because the load haven't finished yet 
          // (isLoading might not have flipped to true yet in the first tick).
          // However, if we're here and not loading, we'll try to check if it's there.
          if (!channel) {
            // If we've just mounted and haven't loaded yet, return and wait.
            if (!userChannelsLoaded) return;
            
            // If it's still not found, it might be a genuinely missing channel.
            // Clear params and return so we don't open the modal incorrectly.
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openModal');
            newParams.delete('newChannelId');
            newParams.delete('isBridge');
            navigate(`?${newParams.toString()}`, { replace: true });
            return;
          } else if (channel.status === 'syncing' || channel.status === 'pending') {
            // Channel is still syncing, show loading notice instead of modal
            // Removed addPersistentNotice call
            setIsStackedNoticesOpen(true);

            // Clear params from URL now that we've handled the "action"
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openModal');
            newParams.delete('newChannelId');
            newParams.delete('isBridge');
            navigate(`?${newParams.toString()}`, { replace: true });
            return; // Exit effect
          } else {
            // Channel is already connected/active, show success notice instead of modal
            // Removed addPersistentNotice call
            setIsStackedNoticesOpen(true);

            // Clear params from URL now that we've handled the "action"
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openModal');
            newParams.delete('newChannelId');
            newParams.delete('isBridge');
            navigate(`?${newParams.toString()}`, { replace: true });
            return; // Exit effect
          }
        }
        
        setIsSelectChannelSourceModalOpen(true);
      } else if (openModal === 'select_journal_source') {
        setIsSelectJournalSourceModalOpen(true);
      } else if (openContent === 'true') {
        // Fallback for generic open content request (or specific if defined as such)
        // If the user meant "the modal that was open" and we don't know it, 
        // BoardContentModal is the safest "root" modal.
        setIsContentModalOpen(true);
      } else if (tab === 'channels' && channelId) {
        // Find the channel in board channels
        const channel = channels.find(c => c.id === channelId);
        if (channel) {
          // Pseudo-channel rows already carry their scope (sourceEntityId) on
          // the BoardChannel — the entity modal honors that automatically.
          handleOpenChannelEntityModal(channel as any, undefined, 'entities');
          if (entityId) {
            setActiveEntityHighlightId(entityId);
            setShouldHighlightEntity(highlight === 'true');
          }
        }
      }

      // Clear params from URL to prevent reopening on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openModal');
      newParams.delete('openContent');
      newParams.delete('editTitle');
      newParams.delete('tab');
      newParams.delete('channelId');
      newParams.delete('entityId');
      newParams.delete('highlight');
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate, channels, userChannels, userChannelsLoaded, userChannelsLoading]);


  useEffect(() => {
    if (channels.length > 0) {
      const hasMissingConfig = channels.some(c => c.channelType && !c.channelTypeConfig);
      if (hasMissingConfig) {
        queryClient.invalidateQueries({ queryKey: ["board-payload"] });
}
    }
  }, [channels, queryClient]);



  // Clear board state when component unmounts or board changes
  useEffect(() => {
    return () => {
      clearBoardState();
    };
  }, [id, clearBoardState]);

  // Use custom hook for entity selection or other logic if needed
  // (useBoardName is already called at the top)

  // Handle channel selection from ChannelEntityModal
  const handleSelectChannel = React.useCallback(async (channel: Channel) => {
    if (!id) {
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.noBoardId") as string,
      });
      return;
    }

    // Set the active channel immediately
    setActiveEntityChannel(channel);

    // Check if channel is already in the working list
    const existingWorkingChannel = workingChannels.find((c) => c.id === channel.id);

    if (existingWorkingChannel && (existingWorkingChannel as any).entities && (existingWorkingChannel as any).entities.length > 0) {
      // If already in workingChannels and has entities, nothing more to do
      // The unified modal will switch to entity view based on activeEntityChannel
      return;
    }

    // Add channel to working state with loading state if not already there or entities are missing
    if (!existingWorkingChannel) {
      const channelWithLoading = {
        ...channel,
        isLoadingEntities: true,
        entities: [],
      };
      setWorkingChannels((prev) => [...prev, channelWithLoading]);
    } else if (!(existingWorkingChannel as any).entities || (existingWorkingChannel as any).entities.length === 0) {
      // Update existing channel to loading state if entities are missing
      setWorkingChannels((prev) =>
        prev.map((c) =>
          c.id === channel.id ? { ...c, isLoadingEntities: true, entities: [] } : c
        )
      );
    }

    // Fetch entities in the background
    try {
      const response = await channelsService.getEntities(channel.id);

      if (response.success && response.data) {
        // Parse the API response and add the elements to the channel
        setWorkingChannels((prev) =>
          prev.map((c) =>
            c.id === channel.id
              ? {
                ...c,
                isLoadingEntities: false,
                entities: response.data,
                elements: response.data,
              }
              : c
          )
        );
        // Update the activeEntityChannel with fetched entities
        setActiveEntityChannel((prev) => prev ? {
          ...prev,
          isLoadingEntities: false,
          entities: response.data,
          elements: response.data,
        } : null);
      }
    } catch (error) {
      console.error("Error fetching channel entities:", error);
      // Remove loading state even on error
      setWorkingChannels((prev) =>
        prev.map((c) =>
          c.id === channel.id ? { ...c, isLoadingEntities: false } : c
        )
      );
      setActiveEntityChannel((prev) => prev ? { ...prev, isLoadingEntities: false } : null);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.failedToLoadChannel") as string,
      });
    }
  }, [id, workingChannels, showAlert, t]);

  // (Removed incorrect duplicate)



  // Open entity selection modal with a specific channel pre-selected
  // This is called when clicking on a channel group in the main page
  const handleOpenChannelEntityModal = React.useCallback(async (
    channel?: BoardChannel,
    rootEntityId?: string,
    viewMode: 'channels' | 'entities' = 'entities'
  ) => {
    // Initialize working state from store
    if (channels.length > 0) {
      const storeEntities = useBoardStore.getState().entities;

      const channelsWithEntities = channels.map((ch) => {
        const channelEntities = storeEntities.filter(
          (e) => e.channelId === ch.id
        );

        return {
          ...ch,
          entities: channelEntities,
          elements: channelEntities,
        };
      });
      setWorkingChannels(channelsWithEntities as any[]);

      const allSelectedIds = storeEntities
        .filter((e) => e.isSelected)
        .map((e) => e.id);
      setWorkingSelectedEntityIds(allSelectedIds);

      setInitialChannelIds(new Set(channels.map((c) => c.id)));
      setInitialSelectedEntityIds(new Set(allSelectedIds));

      // Populating operations state
      const storeOperationIds = useBoardStore.getState().selectedOperationIds || [];
      setWorkingSelectedOperationIds(storeOperationIds);
      setInitialSelectedOperationIds(new Set(storeOperationIds));
    } else {
      setInitialChannelIds(new Set());
      setInitialSelectedEntityIds(new Set());
      setWorkingSelectedOperationIds([]);
      setInitialSelectedOperationIds(new Set());
    }

    // If a specific channel was provided, set it as active and fetch its entities
    let scopeForRow = rootEntityId;
    if (channel) {
      // Convert BoardChannel to Channel-like object for the modal
      const channelForModal = channel as unknown as Channel;
      setActiveEntityChannel(channelForModal);

      // For pseudo BoardChannel rows, scope the entity tree to descendants of
      // the row's source entity (the underlying ad account).
      if (!scopeForRow && channel.isPseudoChannel && channel.sourceEntityId) {
        scopeForRow = channel.sourceEntityId;
      }

      // Trigger entity fetch for this channel
      handleSelectChannel(channelForModal);
    }

    // Open the modal
    setActiveEntityRootId(scopeForRow);
    setChannelEntityModalView(viewMode);
    setChannelEntityModalViewOnly(false);
    setIsChannelEntityModalOpen(true);
  }, [channels, handleSelectChannel]);

  const noticeOptions = useMemo(() => ({
    onOpenChannelSource: () => {
      setIsSelectChannelSourceModalOpen(true);
    },
    onSyncComplete: (channelId?: string) => {
      if (channelId) {
        const userChannels = useUserChannelsStore.getState().channels;
        const channel = userChannels.find(c => String(c.id) === String(channelId));
        if (channel && channel.channelTypeConfig?.pseudoChannelEnabled) {
          // Open PseudoChannelPickerModal immediately, fetch entities in background
          setPseudoPickerChannel(channel as Channel);
          setReturnToContentModal(true);
          setIsPseudoPickerOpen(true);
          channelsService.getEntities(channel.id).then(response => {
            if (response.success && response.data) {
              setPseudoPickerChannel(prev => prev && prev.id === channel.id ? { ...prev, entities: response.data } as Channel : prev);
            }
          }).catch(() => {});
          return;
        }
      }
      setIsSelectChannelSourceModalOpen(true);
    },
    onSyncError: (channelId?: string, errorMessage?: string) => {
      const userChannels = useUserChannelsStore.getState().channels;
      const channel = userChannels.find(c => String(c.id) === String(channelId));
      setSyncErrorModal({
        isOpen: true,
        channelName: channel?.name || 'Channel',
        errorMessage: errorMessage
      });
    },
    onAITaskComplete: (payload: any) => {
      // Pick the most "openable" successful action from the plan and route to
      // the corresponding modal/page so the user immediately sees the result.
      const actions: Array<any> = Array.isArray(payload?.actions) ? payload.actions : [];
      const successful = actions.filter((a) => a.status === 'success');
      if (successful.length === 0) return;

      const firstByTool = (tool: string) => successful.find((a) => a.tool === tool);

      const specAction = firstByTool('create_spec');
      if (specAction) {
        const created = specAction.result || {};
        const fromStore = useBoardStore.getState().specs.find((s) => s.id === created.id);
        setEditingSpec(fromStore || created);
        setIsBoardSpecModalOpen(true);
        return;
      }

      const pageAction = firstByTool('create_page') || firstByTool('setup_templated_page');
      if (pageAction?.result?.pageId) {
        useBoardStore.getState().setActivePage(pageAction.result.pageId);
        return;
      }

      const chartsAction = firstByTool('add_charts_to_page');
      if (chartsAction?.result?.pageId) {
        useBoardStore.getState().setActivePage(chartsAction.result.pageId);
        return;
      }

      const journalAction =
        firstByTool('create_journal') ||
        firstByTool('link_journal') ||
        firstByTool('create_operations');
      if (journalAction) {
        setIsContentModalOpen(true);
        return;
      }

      // Default fallback — surface the board content modal so any other change
      // (entity selection, currency, date range) is visible to the user.
      setIsContentModalOpen(true);
    },
    suppressAutoOpen: isContentModalOpen || isCurrencyModalOpen,
  }), [isContentModalOpen, isCurrencyModalOpen]);

  const {
    notices,
    removeNotice,
    isStackedNoticesOpen,
    setIsStackedNoticesOpen,
    activeNoticeIndex,
    isNoticeBarOpen,
    setIsNoticeBarOpen
  } = useBoardNotices(id as string, noticeOptions);


  const handleOpenJournalDetails = (journal: BoardJournal) => {
    setActiveJournalForDetails(journal);
    setIsJournalDetailsModalOpen(true);
  };

  const handleCloseJournalDetailsModal = () => {
    // If the user closes while a background creation is still running, flag it
    // so the post-creation rename prompt is skipped. The API calls still run to
    // completion (the journal gets created and linked), but the UI stays closed.
    if (isCreatingJournalRef.current) {
      journalCreationCancelledRef.current = true;
    }
    setIsJournalDetailsModalOpen(false);
    // Delay clearing the selected journal to avoid a flash during dismissal
    setTimeout(() => {
      setActiveJournalForDetails(null);
    }, 300);
    if (returnToContentModal) {
      setIsContentModalOpen(true);
      setReturnToContentModal(false);
    }
  };

  const handleOpenJournalInFullView = (journalId: string) => {
    const returnToId = id;
    const returnToName = encodeURIComponent(board?.name || 'Board');

    // Close the details modal while the confirmation is shown so the two modals
    // don't fight over the viewport. If the user cancels, reopen the details modal
    // with the same journal selected.
    const journalSnapshot = activeJournalForDetails;
    setIsJournalDetailsModalOpen(false);

    setConfirmationModal({
      isOpen: true,
      title: (t("boards.manageJournalsTitle") as string) || "Go to Journals Library",
      message: (t("boards.manageJournalsMessage") as string) || "To manage this journal in full view, you need to navigate to your journal library for a moment. Don't worry, you can easily come back to your board afterward.",
      confirmText: (t("boards.manageJournalsConfirm") as string) || "Go to Journals",
      cancelText: (t("actions.stay") as string) || "Stay here",
      variant: "primary",
      onConfirm: () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        setPendingNameJournal(null);
        setActiveJournalForDetails(null);
        setReturnToContentModal(false);
        navigate(`/boards?tab=money_journal&journalId=${journalId}&returnToBoardId=${returnToId}&returnToBoardName=${returnToName}`);
      },
      onCancel: () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        // Restore the details modal the user was in.
        if (journalSnapshot) {
          setActiveJournalForDetails(journalSnapshot);
          setIsJournalDetailsModalOpen(true);
        }
      }
    });
  };

  const handleJournalOperationCreatedOnBoard = async (opId: string) => {
    if (!id) return;
    try {
      const currentOpIds = useBoardStore.getState().selectedOperationIds || [];
      if (currentOpIds.includes(opId)) return;
      const updatedOpIds = [...currentOpIds, opId];
      await boardService.updateOperationSelection(id, updatedOpIds);
      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
    } catch (error) {
      console.error('Failed to auto-select new operation on board', error);
    }
  };


  // Handle Add Channel Modal
  const handleOpenAddChannel = () => {
    setConfirmationModal({
      isOpen: true,
      title: t("boards.quitBoardTitle", "Quit Board?") as string,
      message: t("boards.quitBoardToManageChannelsMessage", "You are about to leave the current board to manage your connected channels. Any unsaved layout changes will be lost.") as string,
      confirmText: t("actions.continue", "Continue") as string,
      variant: "primary",
      onConfirm: () => {
        setIsChannelEntityModalOpen(false);
        navigate("/boards?tab=connected_channels&action=add");
      }
    });
  };

  const handleCloseAddChannel = () => {
    setIsAddChannelModalOpen(false);
  };

  const handleAddChannelComplete = (channelId?: string) => {
    setIsAddChannelModalOpen(false);
    setIsSelectChannelSourceModalOpen(false);
    setIsSelectConnectionTypeModalOpen(false);
    
    if (channelId) {
      // Find the channel in user store and auto-add it to board
      const userChannels = useUserChannelsStore.getState().channels;
      const newlyAddedChannel = userChannels.find(c => String(c.id) === String(channelId));
      if (newlyAddedChannel) {
        handleAutoAddChannel(newlyAddedChannel as any);
      } else {
        // Fallback: if not found immediately, reload and show selection
        loadUserChannels(true);
        setIsSelectChannelSourceModalOpen(true);
      }
    } else {
      setIsSelectChannelSourceModalOpen(true);
    }
  };

  // Handle picking a root entity in PseudoChannelPickerModal — backend creates
  // the BoardChannel pseudo row and auto-selects descendants in one transaction.
  // We do an optimistic UI update so the new card appears in the Board Content
  // Modal immediately, then reconcile with the server response.
  const handlePseudoChannelPick = React.useCallback(async (sourceEntityId: string) => {
    if (!id || !pseudoPickerChannel) return;

    setIsPseudoPickerOpen(false);

    if (returnToContentModal) {
      setIsContentModalOpen(true);
      setReturnToContentModal(false);
    }

    // Optimistic add: surface a placeholder BoardChannel row right away so the
    // user sees the new card without waiting for the server round-trip.
    // The `/user/:id/entities` response wraps the array under `.entities`, so
    // we accept either shape ([...] or { entities: [...] }) when resolving the
    // picked source entity's name.
    const sourceEntityName = (() => {
      const raw = (pseudoPickerChannel as unknown as Record<string, unknown>).entities;
      const list: any[] = Array.isArray(raw)
        ? raw
        : (raw && typeof raw === "object" && Array.isArray((raw as any).entities)
            ? (raw as any).entities
            : []);
      const entity = list.find(
        (e: any) => String(e?.id) === String(sourceEntityId),
      ) as { name?: string } | undefined;
      return entity?.name || pseudoPickerChannel.name;
    })();

    const optimisticId = `optimistic-pseudo-${pseudoPickerChannel.id}-${sourceEntityId}`;
    const storeBefore = useBoardStore.getState();
    const alreadyHasRow = storeBefore.channels.some(
      (c) => c.isPseudoChannel && c.sourceEntityId === sourceEntityId,
    );
    if (!alreadyHasRow) {
      useBoardStore.setState({
        channels: [
          ...storeBefore.channels,
          {
            id: optimisticId,
            boardChannelId: optimisticId,
            channelId: pseudoPickerChannel.id,
            pseudoOfChannelId: pseudoPickerChannel.id,
            sourceEntityId,
            isPseudoChannel: true,
            name: sourceEntityName,
            channelType: pseudoPickerChannel.channelType,
            status: pseudoPickerChannel.status || 'active',
            order: storeBefore.channels.length,
            icon: pseudoPickerChannel.icon,
            channelTypeConfig: pseudoPickerChannel.channelTypeConfig,
            isEnabled: true,
          },
        ],
      });
    }

    setIsDataLoading(true);

    try {
      const response = await boardService.addPseudoChannelToBoard(
        id,
        pseudoPickerChannel.id,
        sourceEntityId,
      );

      if (response.success && response.data) {
        const {
          board,
          channels: updatedChannels,
          selectedEntitiesForDisplay,
          stats,
          journals,
          specs,
          finance,
          events,
        } = response.data;
        // The POST /pseudo-channels response goes through getBoardFull, so it
        // already contains finance + events for the new pseudo. We split the
        // update into structure (channels/entities/specs) and finance/events,
        // mirroring exactly what useBoardFullData does on a fresh page load.
        // Splitting matters: setFinanceData also merges spec values and
        // forces a fresh finance reference downstream consumers depend on.
        setBoardData({
          board,
          channels: updatedChannels,
          selectedEntitiesForDisplay,
          stats,
          journals,
          specs,
        });
        if (finance) {
          useBoardStore.getState().setFinanceData(finance);
          if (finance.errors) {
            useBoardStore.getState().mergeChannelErrors(finance.errors);
          }
        }
        if (events) {
          const eventsList = Array.isArray(events) ? events : (events.events || []);
          useBoardStore.getState().setEventsData(eventsList);
        }
        useBoardStore.getState().updateDerivedState();
        // Mark the query stale (so the next mount or window-focus pulls fresh)
        // but DON'T trigger an immediate refetch — the store is already
        // up-to-date from the response we just applied. A refetch here would
        // hit the backend with another full getBoardFull (~3s) for nothing.
        queryClient.invalidateQueries({
          queryKey: ["board-payload", id],
          refetchType: "none",
        });
      }
    } catch (error) {
      console.error("Failed to add pseudo-channel to board", error);
      // Roll back the optimistic insert on failure
      const storeAfter = useBoardStore.getState();
      useBoardStore.setState({
        channels: storeAfter.channels.filter((c) => c.id !== optimisticId),
      });
    } finally {
      // We disabled the auto-refetch above (refetchType: 'none'), so the
      // useBoardFullData effect that normally clears isDataLoading on
      // payloadQuery.isFetching transitions never fires. Clear it here.
      setIsDataLoading(false);
    }
  }, [id, pseudoPickerChannel, returnToContentModal, setBoardData, queryClient, setIsDataLoading]);

  const handleQuitBoardToManageJournals = (source: 'select_journal' | 'onboarding' | 'header' | 'settings') => {
    const closePrev = () => {
      if (source === 'select_journal') setIsSelectJournalSourceModalOpen(false);
      if (source === 'settings') setIsContentModalOpen(false);
    };

    const openPrev = () => {
      if (source === 'select_journal') setIsSelectJournalSourceModalOpen(true);
      if (source === 'settings') setIsContentModalOpen(true);
    };

    closePrev();

    setConfirmationModal({
      isOpen: true,
      title: t("boards.manageJournalsTitle") || "Go to Journals Library",
      message: t("boards.manageJournalsMessage") || "To add a new money journal, you need to navigate to your journal library for a moment. Don't worry, you can easily come back to your board afterward.",
      confirmText: t("boards.manageJournalsConfirm") || "Go to Journals",
      cancelText: t("actions.stay") || "Stay here",
      variant: "primary",
      onConfirm: () => {
        completeStep(OnboardingStep.ADD_DATA);
        const returnToId = id;
        const returnToName = encodeURIComponent(board?.name || 'Board');
        navigate(`/boards?tab=money_journal&action=add&returnToBoardId=${returnToId}&returnToBoardName=${returnToName}`);
      },
      onCancel: openPrev
    });
  };

  // Stable callbacks for modals
  const handleCloseChannelEntityModal = React.useCallback(() => {
    if (confirmationModal.isOpen) return;
    setIsChannelEntityModalOpen(false);
    
    // Delay clearing data to avoid flicker during modal dismissal animation
    setTimeout(() => {
      setActiveEntityChannel(null);
      setActiveEntityRootId(undefined);
      setActiveEntityHighlightId(undefined);
      setShouldHighlightEntity(false);
      setReturnToContentModal(false);
    }, 300);
  }, [confirmationModal.isOpen]);

  const handleClearEntityHighlight = React.useCallback(() => {
    setActiveEntityHighlightId(undefined);
    setShouldHighlightEntity(false);
  }, []);

  const handleChannelEntityModalBack = React.useCallback(() => {
    setIsChannelEntityModalOpen(false);
    
    // Delay clearing data to avoid flicker during modal dismissal animation
    setTimeout(() => {
      setActiveEntityChannel(null);
      setActiveEntityRootId(undefined);
      setActiveEntityJournal(null);
      setActiveEntityHighlightId(undefined);
      setShouldHighlightEntity(false);
    }, 300);

    if (returnToContentModal) {
      setIsContentModalOpen(true);
      return;
    }

    // Existing logic for new channel flow
    const isNewChannel = activeEntityChannel && board?.channels ? !board.channels.some((c: any) => c.id === activeEntityChannel.id) : true;

    if (isNewChannel) {
      setIsSelectChannelSourceModalOpen(true);
    }
  }, [returnToContentModal, activeEntityChannel, board?.channels, navigate]);

  // (Moved higher)

  // Handle toggling entity selection
  const handleToggleEntity = React.useCallback((
    entityIds: string | string[],
    isSelected: boolean
  ) => {
    const idsToToggle = Array.isArray(entityIds) ? entityIds : [entityIds];

    setWorkingSelectedEntityIds((prev) => {
      if (isSelected) {
        // Add IDs that are not already present
        const newIds = idsToToggle.filter((id) => !prev.includes(id));
        return newIds.length > 0 ? [...prev, ...newIds] : prev;
      } else {
        return prev.filter((id) => !idsToToggle.includes(id));
      }
    });
  }, []);

  const handleToggleOperation = React.useCallback((
    operationIds: string | string[],
    isSelected: boolean
  ) => {
    const idsToToggle = Array.isArray(operationIds) ? operationIds : [operationIds];

    setWorkingSelectedOperationIds((prev) => {
      if (isSelected) {
        // Add IDs that are not already present
        const newIds = idsToToggle.filter((id) => !prev.includes(id));
        return newIds.length > 0 ? [...prev, ...newIds] : prev;
      } else {
        // Remove IDs
        return prev.filter((id) => !idsToToggle.includes(id));
      }
    });
  }, []);

  const handleValidateOperationSelection = React.useCallback(async (journalId?: string) => {
    if (!id) return;

    setIsDataLoading(true);
    try {
      // If we are validating a specific journal, check if it has any selections left
      if (journalId) {
        // We need to know which operations belong to this journal to check if *its* selection is empty
        try {
          const response = await boardService.getGlobalJournalOperations(journalId);
          if (Array.isArray(response)) {
            const journalOpIds = response.map((op: any) => op.id);
            const hasSelection = journalOpIds.some(opId => workingSelectedOperationIds.includes(opId));

            if (!hasSelection) {
              // No operations selected for this journal -> Delete/Unlink it
              // We use handleToggleJournal logic which handles unlinking and state update
              await boardService.unlinkJournalFromBoard(id, journalId);
              deleteJournal(journalId);

              // Close modal since we deleted the active item
              setIsChannelEntityModalOpen(false);
            } else {
              // Has selection -> Ensure it is LINKED to the board
              const isLinked = useBoardStore.getState().journals.some(c => c.id === journalId);
              if (!isLinked) {
                // Link it
                try {
                  await boardService.linkJournalToBoard(id, journalId);

                  // Update store locally to reflect change immediately
                  const journal = useJournalStore.getState().journals.find(j => j.id === journalId);
                  if (journal) {
                    useBoardStore.getState().addJournal({ ...journal, type: 'custom', isEnabled: true, order: 0 } as any);
                  }
                } catch (linkError) {
                  console.error("Failed to link journal automatically:", linkError);
                }
              }
            }

          }
        } catch (err) {
          console.error("Error checking journal operations:", err);
          // Proceed with normal update/save if check fails to be safe
        }
      }

      const hasChanges =
        workingSelectedOperationIds.length !== initialSelectedOperationIds.size ||
        !workingSelectedOperationIds.every((id) => initialSelectedOperationIds.has(id));

      if (hasChanges) {
        await boardService.updateOperationSelection(id, workingSelectedOperationIds);

        useBoardStore.getState().setSelectedOperationIds(workingSelectedOperationIds);

        setInitialSelectedOperationIds(new Set(workingSelectedOperationIds));

        // Invalid queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
        useBoardStore.getState().updateDerivedState();
      }

      setIsChannelEntityModalOpen(false);

    } catch (error: any) {
      console.error("Error validating operation selection:", error);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.failedToUpdateOperationSelection") || "Failed to save operations",
      });
      throw error;
    } finally {
      setIsDataLoading(false);
    }
  }, [id, workingSelectedOperationIds, initialSelectedOperationIds, deleteJournal, queryClient, showAlert, t]);



  // Helper to collect all entity IDs recursively
  const collectAllEntityIds = (elements: any[]): string[] => {
    if (!Array.isArray(elements)) return [];
    let ids: string[] = [];
    elements.forEach(el => {
      if (!el) return;
      ids.push(el.id);
      if (el.children && Array.isArray(el.children)) {
        ids = [...ids, ...collectAllEntityIds(el.children)];
      }
    });
    return ids;
  };

  // Count connections on the board. Each BoardChannel row counts as one — pseudo
  // siblings of the same real channel are already represented as separate rows
  // by the backend, so a flat count over `channelList` is correct.
  const calculateTotalConnections = (channelList: any[], _selectedIds: string[], _allEntities: any[]) => {
    return channelList.filter((c) => c.channelType !== "manual").length;
  };

  const handleAutoAddChannel = async (channel: Channel) => {
    if (!id) return;

    // Pseudo-channel-eligible channels open the dedicated picker, which lets
    // the user attach one ad account at a time as its own BoardChannel row.
    if (channel.channelTypeConfig?.pseudoChannelEnabled) {
      setIsSelectChannelSourceModalOpen(false);
      setIsSelectConnectionTypeModalOpen(false);

      const isUrlFlow = searchParams.get('openModal') || searchParams.get('tab');
      if (!isUrlFlow) {
        setReturnToContentModal(true);
      }

      // Open modal immediately with channel (shows loading state)
      setPseudoPickerChannel(channel);
      setIsPseudoPickerOpen(true);

      channelsService.getEntities(channel.id).then(response => {
        if (response.success && response.data) {
          setPseudoPickerChannel(prev => prev && prev.id === channel.id ? { ...prev, entities: response.data } as Channel : prev);
        }
      }).catch(() => {});
      return;
    }

    try {
      // 1. Fetch entities for the channel
      const response = await channelsService.getEntities(channel.id);
      if (!response.success || !response.data) {
        throw new Error("Failed to fetch channel entities");
      }

      // Normalize entities structure (handle potential wrapper)
      let entities = response.data;
      // Some channels might return { entities: [...] } wrapper
      if (!Array.isArray(entities) && typeof entities === 'object' && (entities as any).entities && Array.isArray((entities as any).entities)) {
        entities = (entities as any).entities;
      }

      const newEntityIds = collectAllEntityIds(entities);

      // 2. Get current state
      const currentSelectedEntityIds = useBoardStore.getState().entities
        .filter(e => e.isSelected)
        .map(e => e.id);
      const currentChannelIds = channels.map(c => c.id);

      // Plan Feature Check: Max Channels per Board
      // Exclude manual (Money Journal) connections from the count
      const storeEntities = useBoardStore.getState().entities;
      const currentConnections = calculateTotalConnections(channels, currentSelectedEntityIds, storeEntities);

      const isNewRealChannel = channel.channelType !== 'manual' && !channels.some(c => c.id === channel.id);

      if (isNewRealChannel) {
        const maxChannels = getFeatureValue('max_channels_per_board');
        if (typeof maxChannels === 'number' && maxChannels !== -1 && currentConnections >= maxChannels) {
          setIsSelectChannelSourceModalOpen(false);
          setIsSelectConnectionTypeModalOpen(false);
          setIsContentModalOpen(true);
          setUpgradeModal({
            isOpen: true,
            featureKey: 'max_channels_per_board',
            currentUsage: currentConnections
          });
          return;
        }
      }

      // 3. Prepare new state
      const updatedChannelIds = [...new Set([...currentChannelIds, channel.id])];
      const updatedEntityIds = [...new Set([...currentSelectedEntityIds, ...newEntityIds])];

      // --- OPTIMISTIC UPDATE ---
      setIsSelectChannelSourceModalOpen(false);
      setIsSelectConnectionTypeModalOpen(false);

      const storeState = useBoardStore.getState();

      // Avoid adding duplicate channel if it's already on the board (e.g. re-adding a removed channel)
      const existingChannel = storeState.channels.find(c => c.id === channel.id);
      const optimisticChannels = existingChannel
        ? storeState.channels.map(c => c.id === channel.id ? { ...c, status: 'active' } : c)
        : [...storeState.channels, {
            id: channel.id,
            name: channel.name,
            channelType: channel.channelType,
            status: 'active', // Always 'active' for the board — entity sync state is independent
            order: (channel as any).order,
            icon: channel.icon,
            channelTypeConfig: channel.channelTypeConfig,
            isEnabled: (channel as any).isEnabled ?? true,
          }];

      // Helper to flatten nested entities for optimistic update
      const flattenEntities = (items: any[], parentId: string | null = null): any[] => {
        let flat: any[] = [];
        items.forEach(item => {
          flat.push({
            ...item,
            channelId: channel.id,
            parentId: parentId || item.parentId || null,
            isSelected: true,
            entityType: item.type || item.entityType || 'unknown'
          });
          if (item.children && item.children.length > 0) {
            flat = [...flat, ...flattenEntities(item.children, item.id)];
          }
        });
        return flat;
      };

      const flattenedNewEntities = flattenEntities(entities);
      const optimisticEntities = [
        ...storeState.entities.filter(e => e.channelId !== channel.id), // Remove old version if exists
        ...flattenedNewEntities
      ];

      useBoardStore.setState({
        channels: optimisticChannels as any,
        entities: optimisticEntities as any
      });
      storeState.updateDerivedState();

      // Only open content modal if not coming from URL flow (Traffic Page)
      const isUrlFlow = searchParams.get('openModal') || searchParams.get('tab');
      if (!isUrlFlow) {
        setIsContentModalOpen(true);
      }

      justValidatedRef.current = true;
      // --- END OPTIMISTIC UPDATE ---

      try {
        // 4. Update board
        const boardResponse = await boardService.updateBoardChannelsAndSelections(id, {
          channelIds: updatedChannelIds,
          selectedEntityIds: updatedEntityIds
        });

        if (boardResponse.success && boardResponse.data) {
          const { board, channels: updatedChannels, selectedEntitiesForDisplay, stats, journals, specs } = boardResponse.data;
          // Clear finance and events to avoid ghosting stale data
          setBoardData({
            board,
            channels: updatedChannels,
            selectedEntitiesForDisplay,
            stats,
            journals,
            specs,
            finance: null,
            events: null
          });

          // Refresh other data
          // Refresh the unified board payload
          await queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
          await queryClient.refetchQueries({ queryKey: ["board-payload", id] });
          useBoardStore.getState().updateDerivedState();
        }
      } catch (error: any) {
        console.error("Failed to auto add channel", error);

        let recovered = false;
        const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.toLowerCase().includes('timeout');

        if (isTimeout) {
          try {
            const freshData = await queryClient.fetchQuery({
              queryKey: ["board-structure-refresh", id],
              queryFn: () => boardService.getBoardPayload(id as string),
              staleTime: 0
            });
            if (freshData && freshData.data) {
              const actualChannelIds = freshData.data.channels?.map((c: any) => c.id) || freshData.data.board?.boardChannels?.map((bc: any) => bc.channelId) || [];
              if (actualChannelIds.includes(channel.id)) {
                const { board, channels, selectedEntitiesForDisplay, stats, journals, specs } = freshData.data;
                setBoardData({ board, channels, selectedEntitiesForDisplay, stats, journals, specs });
                useBoardStore.getState().updateDerivedState();
                queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
                queryClient.setQueryData(["board-structure", id], { success: true, data: freshData.data });
                recovered = true;
              }
            }
          } catch (e) {
            console.error("Recovery fetch failed after timeout", e);
          }
        }

        if (!recovered) {
          const originalStructure = queryClient.getQueryData(["board-structure", id]) as any;
          if (originalStructure && originalStructure.data) {
            const prev = originalStructure.data;
            setBoardData({
              board: prev.board,
              channels: prev.channels,
              selectedEntitiesForDisplay: prev.selectedEntitiesForDisplay,
              stats: prev.stats,
              journals: prev.journals,
              specs: prev.specs,
            });
            useBoardStore.getState().updateDerivedState();
          }

          showAlert({
            type: "error",
            title: t("errors.error") as string,
            message: isTimeout ? t("errors.timeout", "Server took too long. Please try again.") as string : t("errors.generic", "An error occurred") as string,
          });
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch channel entities or add channel", error);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.generic", "An error occurred") as string,
      });
    }
  };

  const handleAutoAddJournal = async (journal: any) => {
    if (!id) return;

    try {
      // 1. Link journal to board
      await boardService.linkJournalToBoard(id, journal.id);

      // 2. Fetch operations for this journal (global journal)
      const operations = await boardService.getGlobalJournalOperations(journal.id);
      const newOpIds = operations.map((op: any) => op.id);

      // 3. Get current selected operations
      const currentOpIds = useBoardStore.getState().selectedOperationIds || [];

      // 4. Update selection
      const updatedOpIds = [...new Set([...currentOpIds, ...newOpIds])];
      await boardService.updateOperationSelection(id, updatedOpIds);

      // 5. Refresh board data
      // Close modals first to prevent empty list glitch
      setIsSelectJournalSourceModalOpen(false);
      setIsSelectConnectionTypeModalOpen(false);

      setTimeout(async () => {
        queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
        try {
          // Trigger a refetch of board structure immediately to update UI
          const boardStructure = await queryClient.fetchQuery({
            queryKey: ["board-structure", id],
            queryFn: () => boardService.getBoardPayload(id),
            staleTime: 0
          });

          if (boardStructure && boardStructure.data) {
            setBoardData({
              board: boardStructure.data.board,
              channels: boardStructure.data.channels || (boardStructure.data.boardChannels ? boardStructure.data.boardChannels.map((bc: any) => ({ ...bc.channel, isEnabled: bc.isActive })) : []),
              selectedEntitiesForDisplay: boardStructure.data.selectedEntitiesForDisplay || (boardStructure.data.checkedEntities ? boardStructure.data.checkedEntities.map((ce: any) => ce.entity) : []),
              stats: boardStructure.data.stats,
              journals: boardStructure.data.journals,
              specs: boardStructure.data.specs,
              finance: boardStructure.data.finance,
              events: boardStructure.data.events
            });
          }
        } catch (e) {
          console.error("Failed to refresh board structure in background", e);
        }

        setIsContentModalOpen(true);
      }, 300);

    } catch (error: any) {
      console.error("Failed to auto add journal", error);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.generic", "An error occurred") as string,
      });
    }
  };

  const handleCreateAndOpenJournal = async () => {
    if (!id) return;
    if (isCreatingJournalRef.current) return;

    // Plan Feature Check: Max Journals
    const journalStoreState = useJournalStore.getState();
    const existingJournals = journalStoreState.journals || [];
    const maxJournals = getFeatureValue('max_journals');
    if (maxJournals !== -1 && existingJournals.length >= maxJournals) {
      setUpgradeModal({
        isOpen: true,
        featureKey: 'max_journals',
        currentUsage: existingJournals.length
      });
      return;
    }

    isCreatingJournalRef.current = true;
    journalCreationCancelledRef.current = false;

    // Dismiss the source modals synchronously.
    setIsSelectJournalSourceModalOpen(false);
    setIsSelectConnectionTypeModalOpen(false);
    // Ensure BoardContentModal reopens when the details modal closes so the
    // newly-linked journal is visible in the board content view.
    setReturnToContentModal(true);

    // Generate a unique default name ("Journal 1", "Journal 2", ...)
    const baseName = "Journal";
    let counter = 1;
    const usedNames = new Set(existingJournals.map(j => j.name));
    while (usedNames.has(`${baseName} ${counter}`)) {
      counter++;
    }
    const defaultName = `${baseName} ${counter}`;

    // Open the details modal immediately with a loader, using a placeholder
    // journal so the header (name + icon) renders. The real journal replaces
    // this once the backend returns.
    const placeholderJournal: BoardJournal = {
      id: '__pending__',
      name: defaultName,
      type: 'custom',
      isEnabled: true,
      order: 0,
    };
    setActiveJournalForDetails(placeholderJournal);
    setIsCreatingJournal(true);
    setIsJournalDetailsModalOpen(true);

    // Let the source modal finish its leave transition (Headless UI's
    // Dialog.Transition uses duration-200) before we mutate journalStore —
    // otherwise the new journal briefly flashes into the still-rendered list.
    await new Promise(resolve => setTimeout(resolve, 250));

    try {
      // 1. Create the global journal
      const newJournal = await journalStoreState.addJournal(defaultName);
      if (!newJournal?.id) throw new Error('Failed to create journal');

      // 2. Link it to the current board
      await boardService.linkJournalToBoard(id, newJournal.id);

      // 3. Fetch ops (brand-new journals are empty, but keep the flow symmetric)
      const ops = await boardService.getGlobalJournalOperations(newJournal.id);
      const newOpIds = Array.isArray(ops) ? ops.map((op: any) => op.id) : [];

      // 4. Merge into board's selected operations
      const currentOpIds = useBoardStore.getState().selectedOperationIds || [];
      const updatedOpIds = [...new Set([...currentOpIds, ...newOpIds])];
      if (newOpIds.length > 0) {
        await boardService.updateOperationSelection(id, updatedOpIds);
      }

      // 5. Immediately insert the journal into boardStore so BoardContentModal
      //    reflects it without waiting for a refetch. This mirrors the pattern
      //    the old AddJournalModal used.
      const boardJournalForDetails: BoardJournal = {
        id: newJournal.id,
        name: newJournal.name,
        type: (newJournal.type === 'system' ? 'system' : 'custom') as BoardJournal['type'],
        isEnabled: newJournal.isEnabled ?? true,
        order: newJournal.order ?? 0
      };
      useBoardStore.getState().addJournal(boardJournalForDetails);

      // 6. Invalidate + refetch the active board-payload query so finance /
      //    events / journals are rehydrated from the server.
      await queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
      queryClient.refetchQueries({ queryKey: ["board-payload", id] });

      // 7. Onboarding progression
      completeStep(OnboardingStep.ADD_DATA);

      // 8. Swap the placeholder for the real journal and stop the loader.
      //    If the user closed the modal while we were creating, skip the
      //    rename prompt and leave the modal closed — the journal still
      //    exists and is linked, but we don't reopen anything.
      if (journalCreationCancelledRef.current) {
        setActiveJournalForDetails(null);
      } else {
        setActiveJournalForDetails(boardJournalForDetails);
        setPendingNameJournal(boardJournalForDetails);
      }
    } catch (error: any) {
      console.error("Failed to create and open journal", error);
      setIsJournalDetailsModalOpen(false);
      setActiveJournalForDetails(null);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.generic", "An error occurred") as string,
      });
    } finally {
      setIsCreatingJournal(false);
      isCreatingJournalRef.current = false;
    }
  };

  const handleRemoveChannel = (channel: BoardChannel) => {
    setConfirmationModal({
      isOpen: true,
      title: t("actions.removeChannel") || "Remove Channel",
      message: t("boards.confirmRemoveChannel") || "Are you sure you want to remove this channel? It will be removed from the board along with all its data.",
      confirmText: t("common.remove") || "Remove",
      variant: "danger",
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        if (!id) return;

        try {
          // Pseudo-channel: detach the BoardChannel row directly (backend
          // removes its descendants from BoardCheckedEntity in one transaction).
          if (channel.isPseudoChannel) {
            const response = await boardService.removePseudoChannelFromBoard(
              id,
              channel.boardChannelId ?? channel.id,
            );
            if (response.success && response.data) {
              const { board, channels: updatedChannels, selectedEntitiesForDisplay, stats, journals, specs } = response.data;
              setBoardData({
                board,
                channels: updatedChannels,
                selectedEntitiesForDisplay,
                stats,
                journals,
                specs,
                finance: undefined,
                events: undefined,
              });
              queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
              useBoardStore.getState().updateDerivedState();
              setIsContentModalOpen(true);
            }
            return;
          }

          // Normal channel: drop it from the board's channelIds, drop its
          // selected entities. Other channels' selections are preserved.
          const realChannelId = channel.channelId ?? channel.id;
          const idsToRemove = useBoardStore.getState().entities
            .filter(e => e.channelId === realChannelId && e.isSelected)
            .map(e => e.id);

          const currentSelectedEntityIds = useBoardStore.getState().entities
            .filter(e => e.isSelected)
            .map(e => e.id);
          const newSelectedEntityIds = currentSelectedEntityIds.filter(eid => !idsToRemove.includes(eid));
          const newChannelIds = channels
            .filter(c => !c.isPseudoChannel && (c.channelId ?? c.id) !== realChannelId)
            .map(c => c.channelId ?? c.id);

          const boardResponse = await boardService.updateBoardChannelsAndSelections(id, {
            channelIds: newChannelIds,
            selectedEntityIds: newSelectedEntityIds,
          });

          if (boardResponse.success && boardResponse.data) {
            const { board, channels: updatedChannels, selectedEntitiesForDisplay, stats, journals, specs } = boardResponse.data;
            setBoardData({
              board,
              channels: updatedChannels,
              selectedEntitiesForDisplay,
              stats,
              journals,
              specs,
              finance: undefined,
              events: undefined,
            });
            queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
            useBoardStore.getState().updateDerivedState();
            setIsContentModalOpen(true);
          }
        } catch (error) {
          console.error("Failed to remove channel", error);
          showAlert({
            type: "error",
            title: t("errors.error") as string,
            message: "Failed to remove channel",
          });
        }
      },
      onCancel: () => {
        setIsContentModalOpen(true);
      }
    });
  };

  const handleRemoveJournal = (journalId: string) => {
    setConfirmationModal({
      isOpen: true,
      title: t("actions.removeJournal") || "Remove Journal",
      message: t("boards.confirmRemoveJournal") || "Are you sure you want to remove this journal from the board?",
      confirmText: t("common.remove") || "Remove",
      variant: "danger",
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        if (!id) return;

        try {
          await boardService.unlinkJournalFromBoard(id, journalId);
          deleteJournal(journalId);

          queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
          // Ensure content modal is open
          setIsContentModalOpen(true);
        } catch (error) {
          console.error("Failed to remove journal", error);
          showAlert({
            type: "error",
            title: t("errors.error") as string,
            message: "Failed to remove journal",
          });
        }
      },
      onCancel: () => {
        setIsContentModalOpen(true);
      }
    });
  };

  // Handle entity selection validation
  const handleValidateEntitySelection = React.useCallback(async (overrideEntityIds?: string[]) => {
    if (!id) return;

    // Show board loader during selection update
    setIsDataLoading(true);

    try {
      // Use overrides if provided, otherwise working state
      let entityIdsToCheck = overrideEntityIds || workingSelectedEntityIds;

      // Filter out rootEntityId to avoid backend assuming all children are selected
      if (activeEntityRootId) {
        entityIdsToCheck = entityIdsToCheck.filter(id => String(id) !== String(activeEntityRootId));

        // Also filter out any ancestors of the root entity, otherwise the backend will
        // auto-select their descendants when saving partial selections
        if (activeEntityChannel) {
          let entities = (activeEntityChannel as any).entities;
          if (!Array.isArray(entities) && typeof entities === 'object' && entities?.entities && Array.isArray(entities.entities)) {
            entities = entities.entities;
          }

          if (Array.isArray(entities)) {
            const flatten = (arr: any[]): any[] => {
              if (!Array.isArray(arr)) return [];
              return arr.reduce((acc, val) =>
                Array.isArray(val.children) ? acc.concat([val, ...flatten(val.children)]) : acc.concat(val), []);
            };

            const flatEntities = flatten(entities);
            let currentId: string | undefined = activeEntityRootId;
            let failsafe = 0;

            while (currentId && failsafe < 50) {
              failsafe++;
              const entity = flatEntities.find((e: any) => String(e.id) === String(currentId));
              if (entity && entity.parentId) {
                const pId = String(entity.parentId);
                entityIdsToCheck = entityIdsToCheck.filter(id => String(id) !== pId);
                currentId = pId;
              } else {
                currentId = undefined;
              }
            }
          }
        }
      }

      // Get all channel IDs from working state
      let channelIds = workingChannels.map((c) => c.id);

      // Filter out channels that have NO entities selected
      // We iterate through workingChannels, check their entities against entityIdsToCheck
      channelIds = channelIds.filter(channelId => {
        const channel = workingChannels.find(c => c.id === channelId);
        if (!channel || !(channel as any).entities) return false; // Should not happen for working channels

        // Normalize entities structure (handle potential wrapper)
        let entities = (channel as any).entities;
        if (!Array.isArray(entities) && typeof entities === 'object' && entities.entities && Array.isArray(entities.entities)) {
          entities = (entities as any).entities;
        }

        // Flatten entities to check IDs
        const collectIds = (elements: any[]): string[] => {
          if (!Array.isArray(elements)) return [];
          let ids: string[] = [];
          elements.forEach(el => {
            if (!el) return;
            ids.push(el.id);
            if (el.children && Array.isArray(el.children)) {
              ids = [...ids, ...collectIds(el.children)];
            }
          });
          return ids;
        };

        const channelEntityIds = collectIds(entities);
        // Keep channel only if at least one of its entities is selected
        return channelEntityIds.some(eId => entityIdsToCheck.includes(eId));
      });

      // Plan Feature Check: Max Channels per Board (Bug 1 & 2)
      const maxChannels = getFeatureValue('max_channels_per_board');
      if (typeof maxChannels === 'number' && maxChannels !== -1) {
        const storeEntities = useBoardStore.getState().entities;
        const previousSelectedIds = storeEntities.filter(e => e.isSelected).map(e => e.id);
        const previousConnections = calculateTotalConnections(channels, previousSelectedIds, storeEntities);
        const newConnections = calculateTotalConnections(workingChannels, entityIdsToCheck, storeEntities);

        // Only block if we are actually Increasing the connection count or staying above limit
        if (newConnections > maxChannels && newConnections > previousConnections) {
          setIsChannelEntityModalOpen(false);
          if (returnToContentModal) {
            setIsContentModalOpen(true);
          }
          setUpgradeModal({
            isOpen: true,
            featureKey: 'max_channels_per_board',
            currentUsage: previousConnections
          });
          return;
        }
      }

      // Check for changes before making API call
      // Note: We compare against initialChannelIds. If a channel was filtered out above, it will count as a change (removal)
      const hasChannelChanges =
        channelIds.length !== initialChannelIds.size ||
        !channelIds.every((id) => initialChannelIds.has(id));

      const hasEntityChanges =
        entityIdsToCheck.length !== initialSelectedEntityIds.size ||
        !entityIdsToCheck.every((id) =>
          initialSelectedEntityIds.has(id)
        );

      if (!hasChannelChanges && !hasEntityChanges && !overrideEntityIds) {
        setIsChannelEntityModalOpen(false);
        setWorkingChannels([]);
        setWorkingSelectedEntityIds([]);
        // Mark that we just validated to prevent auto-reopen
        justValidatedRef.current = true;
        return;
      }

      // --- OPTIMISTIC UPDATE ---
      setIsChannelEntityModalOpen(false);

      const storeState = useBoardStore.getState();

      // Determine optimistic channels — strip working-state extras (entities, elements,
      // isLoadingEntities) so the store channels stay in the same shape the backend returns.
      // Keeping those extras causes channelGroups to briefly see a mismatched structure,
      // which can flash an extra card for bridge channels.
      const sanitizeChannel = (ch: any, fallbackStatus = 'active') => ({
        id: ch.id,
        name: ch.name,
        channelType: ch.channelType,
        status: ch.status || fallbackStatus,
        order: ch.order,
        icon: ch.icon,
        channelTypeConfig: ch.channelTypeConfig,
        isEnabled: ch.isEnabled ?? true,
      });
      const optimisticChannels = channelIds.map(cid => {
        // Prefer existing store channel (has the real status), fall back to working channel
        const existing = storeState.channels.find((c: any) => c.id === cid);
        if (existing) return sanitizeChannel(existing);
        const wc = workingChannels.find((c: any) => c.id === cid);
        if (wc) return sanitizeChannel(wc, 'active');
        return { id: cid, status: 'active', name: 'Loading...' } as any;
      });

      // Compute optimistic entities based on entityIdsToCheck
      const optimisticEntities = storeState.entities.map(e => ({
        ...e,
        isSelected: entityIdsToCheck.includes(e.id)
      }));

      // Find any newly selected entities from working channels that might not exist in store.entities yet
      workingChannels.forEach((wc: any) => {
        // Handle both flat and nested structures representing entities
        let rawEntities = wc.entities;
        if (rawEntities && !Array.isArray(rawEntities) && Array.isArray((rawEntities as any).entities)) {
          rawEntities = (rawEntities as any).entities;
        }

        const flatten = (arr: any[]): any[] => {
          if (!Array.isArray(arr)) return [];
          return arr.reduce((acc, val) =>
            Array.isArray(val.children) ? acc.concat([val, ...flatten(val.children)]) : acc.concat(val), []);
        };

        const flatWorking = flatten(rawEntities || []);
        flatWorking.forEach(we => {
          if (entityIdsToCheck.includes(we.id) && !optimisticEntities.some(oe => oe.id === we.id)) {
            optimisticEntities.push({
              ...we,
              channelId: wc.id,
              isSelected: true,
              entityType: we.type || we.entityType || 'unknown'
            });
          }
        });
      });

      // Update store immediately with optimistic data
      useBoardStore.setState({
        channels: optimisticChannels as any,
        entities: optimisticEntities as any
      });
      storeState.updateDerivedState();

      // Clear working state completely
      setWorkingChannels([]);
      setWorkingSelectedEntityIds([]);
      setActiveEntityChannel(null); // Clean up active channel

      justValidatedRef.current = true;
      if (returnToContentModal) {
        setIsContentModalOpen(true);
        // We can safely reset the flag after consuming it
        setReturnToContentModal(false);
      }

      // --- END OPTIMISTIC UPDATE ---

      try {
        // Call API to update channels and selections with full=false (optimized response)
        const response = await boardService.updateBoardChannelsAndSelections(id, {
          channelIds: channelIds,
          selectedEntityIds: entityIdsToCheck,
        });

        // Use the board data from the response (optimization: only structure returned for speed)
        if (response.success && response.data) {
          const { board, channels, selectedEntitiesForDisplay, stats, journals, specs } = response.data;

          // Update the store with structure only — keep existing finance/events
          // to prevent zero-value flash until the refetch below completes.
          setBoardData({
            board,
            channels,
            selectedEntitiesForDisplay,
            stats,
            journals,
            specs,
            finance: undefined,
            events: undefined
          });

          // Trigger refetch for the unified board payload
          console.log(`[BoardPage] Invalidating payload for board ${id} after selection change`);
          await queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
          await queryClient.refetchQueries({ queryKey: ["board-payload", id] });

          // Immediately trigger derived state update to ensure selectedEntities are recalculated
          useBoardStore.getState().updateDerivedState();
        }
      } catch (error: any) {
        console.error("Error validating selection:", error);

        let recovered = false;
        const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.toLowerCase().includes('timeout');

        if (isTimeout) {
          try {
            // Attempt to recover by fetching structure manually
            const freshData = await queryClient.fetchQuery({
              queryKey: ["board-structure-refresh", id],
              queryFn: () => boardService.getBoardPayload(id as string),
              staleTime: 0
            });
            if (freshData && freshData.data) {
              const actualChannelIds = freshData.data.channels?.map((c: any) => c.id) || freshData.data.board?.boardChannels?.map((bc: any) => bc.channelId) || [];
              const didApply = channelIds.every(cid => actualChannelIds.includes(cid));

              if (didApply) {
                const { board, channels, selectedEntitiesForDisplay, stats, journals, specs } = freshData.data;
                setBoardData({
                  board, channels, selectedEntitiesForDisplay, stats, journals, specs
                });
                useBoardStore.getState().updateDerivedState();
                queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
                queryClient.setQueryData(["board-structure", id], {
                  success: true,
                  data: freshData.data
                });
                recovered = true;
              }
            }
          } catch (e) {
            console.error("Recovery fetch failed after timeout", e);
          }
        }

        if (!recovered) {
          // Revert to original backup via query data
          const originalStructure = queryClient.getQueryData(["board-structure", id]) as any;
          if (originalStructure && originalStructure.data) {
            const prev = originalStructure.data;
            setBoardData({
              board: prev.board,
              channels: prev.channels,
              selectedEntitiesForDisplay: prev.selectedEntitiesForDisplay,
              stats: prev.stats,
              journals: prev.journals,
              specs: prev.specs,
            });
            useBoardStore.getState().updateDerivedState();
          }

          showAlert({
            type: "error",
            title: t("errors.error") as string,
            message: isTimeout ? t("errors.timeout", "Server took too long. Please try again.") as string : t("errors.failedToUpdateEntitySelection") as string,
          });
        }
      }
    } catch (error: any) {
      console.error("Error validating selection wrapping block:", error);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: t("errors.failedToUpdateEntitySelection") as string,
      });
      throw error;
    } finally {
      setIsDataLoading(false);
    }
  }, [id, activeEntityRootId, activeEntityChannel, workingChannels, workingSelectedEntityIds, initialChannelIds, initialSelectedEntityIds, returnToContentModal, queryClient, setBoardData, showAlert, t]);

  // Page Management


  function handleSelectPage(pageId: string) {
    if (isEditMode) {
      showAlert({ type: 'warning', title: t('boards.alerts.editModeTitle'), message: t('boards.alerts.editModeMessage') });
      return;
    }
    setActivePage(pageId);
  }

  const handleUpdatePage = async (pageId: string, name: string) => {
    if (!id) return;
    try {
      await boardService.updatePage(id, pageId, { name });
      updatePage(pageId, { name });
    } catch (e) {
      console.error(e);
      showAlert({ type: 'error', title: t('errors.error') as string, message: t('pages.unableToRename') as string });
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!id) return;

    // Safety check: Don't delete if only page? Or depends on requirements. 
    // Usually fine to have 0 pages (Main Board always exists).

    setConfirmationModal({
      isOpen: true,
      title: t('boards.alerts.deletePageTitle'),
      message: t('boards.alerts.deletePageMessage'),
      onConfirm: async () => {
        try {
          await boardService.deletePage(id, pageId);
          // The store's deletePage also handles switching active page if needed
          useBoardStore.getState().deletePage(pageId);
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (e) {
          console.error(e);
          showAlert({ type: 'error', title: t('errors.error'), message: t('boards.alerts.deletePageError') });
        }
      }
    });
  };

  const handleReorderPages = async (orderedPageIds: string[]) => {
    if (!id) return;

    // Update the store immediately for responsive UI
    useBoardStore.getState().reorderPages(orderedPageIds);

    // Persist to backend
    try {
      await boardService.reorderPages(id, orderedPageIds);
    } catch (e) {
      console.error(e);
      showAlert({ type: 'error', title: t('errors.error'), message: t('boards.alerts.reorderPagesError') });
      // Optionally, you could revert the store state here
    }
  };

  // Edit Mode State
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const [savedLayout, setSavedLayout] = useState<GridItem[]>([]);

  // Edit Mode Animation State (manages smooth exit for both mobile and desktop)

  const handleEditModeCancel = () => {
    handleCancelLayoutChanges();
  };

  const handleEditModeSave = () => {
    handleSaveLayoutChanges();
  };

  // Confirmation Modal State (moved higher to avoid use-before-declaration)

  // Chart Layout State
  const [layoutItems, setLayoutItems] = useState<GridItem[]>([]);
  // Track which page the current layout items belong to
  // This prevents stale items from being rendered when switching pages
  const lastActivePageIdRef = useRef(activePageId);
  const isPageSwitching = lastActivePageIdRef.current !== activePageId;

  // Browser-level alert for refreshing or exiting in edit mode
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditMode) {
        const message = t("boards.unsavedChangesAlert") || "You have unsaved changes. Are you sure you want to leave?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditMode, t]);

  const handleBackToExplorer = () => {
    if (isEditMode) {
      setConfirmationModal({
        isOpen: true,
        title: t("boards.quitBoardTitle") || "Quit Board?",
        message: t("boards.quitBoardMessage") || "You are about to leave the current board. Any unsaved layout changes will be lost.",
        confirmText: t("actions.continue") || "Quit Board",
        cancelText: t("actions.stay") || "Stay",
        variant: "primary",
        onConfirm: () => {
          setIsEditMode(false);
          completeStep(OnboardingStep.ADD_DATA);
          navigate("/boards");
        }
      });
    } else {
      navigate("/boards");
    }
  };
  const safeLayoutItems = isPageSwitching ? [] : layoutItems;


  const [droppingItem, setDroppingItem] = useState<
    { i: string; w: number; h: number } | undefined
  >(undefined);

  const handleChartDragStart = (_type: string, sizeTier: SizeTier, _aspectRatio: string) => {
    const currentBp = detectBreakpoint(window.innerWidth);
    const w = calculateChartWidth(sizeTier, currentBp);
    const h = calculateChartHeight(sizeTier, currentBp);

    setDroppingItem({ i: "__dropping-elem__", w, h });
  };

  // History State
  const [history, setHistory] = useState<GridItem[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const addToHistory = (newItems: GridItem[]) => {
    setHistory(prev => {
      const currentHistory = prev.slice(0, historyIndex + 1);
      const nextHistory = [...currentHistory, newItems];
      if (nextHistory.length > 10) nextHistory.shift();
      return nextHistory;
    });
    setHistoryIndex(prev => {
      // If we are capping at 10, the max index is 9.
      // If length was < 10, index increases by 1.
      // If prev is 9, it stays 9.
      return Math.min(prev + 1, 9);
    });
  };

  const scrollToAndHighlight = (itemId: string) => {
    // Wait for render
    setTimeout(() => {
      const element = document.querySelector(`[data-chart-id="${itemId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Wait for scroll (approx) then blink
        setTimeout(() => {
          element.classList.add('chart-blink');
          setTimeout(() => {
            element.classList.remove('chart-blink');
          }, 1000);
        }, 300);
      }
    }, 100);
  };

  const findSignificantChange = (current: GridItem[], next: GridItem[]): string | null => {
    // 1. Check for new items (Added / Re-appeared)
    const currentIds = new Set(current.map(i => i.i));

    const addedItem = next.find(i => !currentIds.has(i.i));
    if (addedItem) return addedItem.i;

    // 2. Check for moved/resized items
    for (const nextItem of next) {
      const currentItem = current.find(i => i.i === nextItem.i);
      if (currentItem) {
        if (currentItem.x !== nextItem.x || currentItem.y !== nextItem.y ||
          currentItem.w !== nextItem.w || currentItem.h !== nextItem.h) {
          return nextItem.i;
        }
      }
    }

    return null;
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const targetLayout = history[newIndex];
      const changeId = findSignificantChange(layoutItems, targetLayout);

      setHistoryIndex(newIndex);
      setLayoutItems(targetLayout);

      if (changeId) {
        scrollToAndHighlight(changeId);
      }
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const targetLayout = history[newIndex];
      const changeId = findSignificantChange(layoutItems, targetLayout);

      setHistoryIndex(newIndex);
      setLayoutItems(targetLayout);

      if (changeId) {
        scrollToAndHighlight(changeId);
      }
    }
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if input/textarea is focused for most shortcuts
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isTyping) {
        // Only Escape should work while typing in some cases (like AI prompt)
        if (e.key === 'Escape') {
          if (isAITaskBannerOpen) {
            setIsAITaskBannerOpen(false);
            return;
          }
        }
        return;
      }

      if (e.key === 'Escape') {
        if (isAITaskBannerOpen) {
          setIsAITaskBannerOpen(false);
          return;
        }

        if (isEditMode) {
          handleCancelLayoutChanges();
          return;
        }
      }

      // Space key to toggle AI banner
      if (e.key === ' ') {
        if (!isAITaskBannerOpen) {
          // Check for any open modal or notice
          const isAnyOverlappingUIOpen =
            isChannelEntityModalOpen ||
            isContentModalOpen ||
            isCurrencyModalOpen ||
            isAddChannelModalOpen ||
            isChartsModalOpen ||
            isTemplatesModalOpen ||
            isPageTypeModalOpen ||
            isSaveTemplateModalOpen ||
            isBoardSpecModalOpen ||
            isSelectConnectionTypeModalOpen ||
            isSelectChannelSourceModalOpen ||
            isSelectJournalSourceModalOpen ||
            confirmationModal.isOpen ||
            aiTaskConfirmationModal.isOpen ||
            upgradeModal.isOpen ||
            dateRangeUpgrade.isOpen ||
            featureExplanation.isOpen ||
            syncErrorModal.isOpen ||
            isNoticeBarOpen ||
            isStackedNoticesOpen ||
            isDatePickerOpen;

          if (!isAnyOverlappingUIOpen) {
            e.preventDefault();
            setIsAITaskBannerOpen(true);
          }
        } else if (!hasAIInputChanged) {
          e.preventDefault();
          setIsAITaskBannerOpen(false);
        }
      }

      if (isEditMode) {
        // Undo/Redo
        if (e.key.toLowerCase() === 'z') {
          if (e.metaKey || e.ctrlKey) {
            if (e.shiftKey) {
              redo(); // Cmd/Ctrl + Shift + Z
            } else {
              undo(); // Cmd/Ctrl + Z
            }
            e.preventDefault();
          }
        } else if (e.key.toLowerCase() === 'y') {
          if (e.ctrlKey) {
            // Only Ctrl+Y for Windows Redo (avoid Cmd+Y on Mac which is History)
            redo();
            e.preventDefault();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isEditMode, history, historyIndex,
    isAITaskBannerOpen, aiMode, hasAIInputChanged,
    isChannelEntityModalOpen, isContentModalOpen, isCurrencyModalOpen,
    isAddChannelModalOpen, isChartsModalOpen, isTemplatesModalOpen,
    isPageTypeModalOpen, isSaveTemplateModalOpen,
    isBoardSpecModalOpen, isSelectConnectionTypeModalOpen,
    isSelectChannelSourceModalOpen, isSelectJournalSourceModalOpen,
    aiTaskConfirmationModal.isOpen, isNoticeBarOpen,
    isStackedNoticesOpen, isDatePickerOpen
  ]); // Dependencies are crucial here

  const handleChartDragStartFromModal = (type: string, sizeTier: SizeTier, aspectRatio: string) => {
    // Perform standard drag start
    handleChartDragStart(type, sizeTier, aspectRatio);

    // Close modal completely instead of hiding - HeadlessUI Dialog's portal/focus-trap
    // blocks pointer events even with pointer-events-none. Closing unmounts it entirely.
    // The browser has already captured the drag at this point, so this is safe.
    setTimeout(() => {
      setIsChartsModalOpen(false);
      setIsChartsModalHidden(false);
      shouldReopenChartsModalRef.current = true;
    }, 0);
  };

  const handleChartDragEnd = () => {
    // Clear dropping item when drag ends (whether picked or not)
    setDroppingItem(undefined);
    // Reset hidden state (in case it was set)
    setIsChartsModalHidden(false);

    // Reopen modal if it was marked to be reopened (e.g. dragged from modal)
    if (shouldReopenChartsModalRef.current) {
      setIsChartsModalOpen(true);
      shouldReopenChartsModalRef.current = false;
    }

    // Double ensure with timeout
    setTimeout(() => setDroppingItem(undefined), 0);
  };

  // Only update state when layout change is finished (drag or resize stop)
  // Key fix: Do NOT re-run generateResponsiveLayouts. Use RGL positions directly.
  // This ensures resize preserves exact size, drag keeps position, and other charts don't move.
  const handleLayoutChangeComplete = (layout: Layout[], activeItemId?: string, isResize?: boolean) => {
    const currentBp = detectBreakpoint(window.innerWidth);

    const newItems = layoutItems.map((item) => {
      const layoutItem = layout.find((l) => l.i === item.i);
      if (layoutItem) {
        // Only recalculate SizeTier if this is the item being RESIZED (not dragged)
        if (item.i === activeItemId && isResize) {
          // Find closest SizeTier by comparing against CHART_WIDTH_MAP at the current breakpoint.
          // This ensures that resizing on a small screen correctly identifies the tier
          // so it reflows properly on larger screens.
          let closestTier = item.sizeTier;
          let minDistance = Infinity;

          Object.entries(CHART_WIDTH_MAP).forEach(([tier, bpMap]) => {
            const widthAtBp = bpMap[currentBp] ?? SIZE_TIERS[tier as SizeTier].preferred;
            const distance = Math.abs(widthAtBp - layoutItem.w);
            if (distance < minDistance) {
              minDistance = distance;
              closestTier = tier as SizeTier;
            } else if (distance === minDistance) {
              // On tie, prefer the SMALLEST tier (most conservative for reflow).
              if (SIZE_TIERS[tier as SizeTier].preferred < SIZE_TIERS[closestTier].preferred) {
                closestTier = tier as SizeTier;
              }
            }
          });

          return {
            ...item,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            sizeTier: closestTier,
            aspectRatio: `${layoutItem.w}:${layoutItem.h}`,
            _editBp: currentBp,
          };
        } else {
          // For non-active items (or dragged active item):
          // Take x/y/w/h from RGL to stay consistent with what's on screen.
          // Do NOT recalculate sizeTier — preserve existing semantic size.
          return {
            ...item,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            _editBp: currentBp,
          };
        }
      }
      return { ...item, _editBp: currentBp };
    });

    // Apply horizontal compaction ("Gravity Left") before sorting
    // We only compact the manipulated item if provided, otherwise global
    const compactedItems = compactHorizontal(newItems, activeItemId);

    // Reorder based on new layout positions
    const sortedByPosition = [...compactedItems].sort((a, b) => {
      const ay = Math.round(a.y);
      const by = Math.round(b.y);
      if (ay === by) return a.x - b.x;
      return ay - by;
    });

    const reorderedItems = sortedByPosition.map((item, index) => ({
      ...item,
      order: index
    }));

    // Use positions directly from RGL — don't re-run flow engine
    setLayoutItems(reorderedItems);
    addToHistory(reorderedItems);
  };

  const handleDragStop = (layout: Layout[], _oldItem: Layout, newItem: Layout) => {
    handleLayoutChangeComplete(layout, newItem.i, false);
  };

  const handleResizeStop = (layout: Layout[], _oldItem: Layout, newItem: Layout) => {
    handleLayoutChangeComplete(layout, newItem.i, true);
  };

  const handleDrop = (_layout: Layout[], layoutItem: Layout, event: Event) => {
    // If board is in lock mode, automatically switch to build mode
    const wasEditMode = isEditMode;
    if (!isEditMode) {
      setSavedLayout([...layoutItems]);
      setIsEditMode(true);
    }

    const dragEvent = event as DragEvent;
    const type = dragEvent.dataTransfer?.getData("text/plain");
    const currentBp = detectBreakpoint(window.innerWidth);

    if (type) {
      const chartDef = CHART_DEFINITIONS.find((c) => c.type === type);
      if (chartDef) {
        // Create new item with defaults from definition
        const newItem: GridItem = {
          ...layoutItem,
          i: `${type}-${Date.now()}`,
          type: type,
          sizeTier: chartDef.defaultSizeTier,
          aspectRatio: chartDef.defaultAspectRatio,
          order: layoutItems.length,
          _editBp: currentBp,
        };

        // Use positions directly from RGL layout (not re-computed)
        const updatedItems = layoutItems.map((item) => {
          const layoutMatch = _layout.find((l) => l.i === item.i);
          if (layoutMatch) {
            return {
              ...item,
              x: layoutMatch.x,
              y: layoutMatch.y,
              w: layoutMatch.w,
              h: layoutMatch.h,
              _editBp: currentBp,
            };
          }
          return { ...item, _editBp: currentBp };
        });

        const combined = [...updatedItems, newItem];

        // Apply horizontal compaction on drop too
        const compacted = compactHorizontal(combined, newItem.i);

        const sorted = [...compacted].sort((a, b) => {
          if (a.y === b.y) return a.x - b.x;
          return a.y - b.y;
        });
        const finalItems = sorted.map((item, index) => ({ ...item, order: index }));

        setLayoutItems(finalItems);

        // Handle history update
        if (!wasEditMode) {
          setHistory([[...layoutItems], finalItems]);
          setHistoryIndex(1);
        } else {
          addToHistory(finalItems);
        }
      }
    }

    // Clear dropping item immediately
    setDroppingItem(undefined);
    setTimeout(() => setDroppingItem(undefined), 0);

    // Reopen charts modal if it was marked to be reopened
    if (shouldReopenChartsModalRef.current) {
      setTimeout(() => {
        setIsChartsModalOpen(true);
        setIsChartsModalHidden(false);
        shouldReopenChartsModalRef.current = false;
      }, 300);
    }
  };

  // Charts Modal Handlers
  const handleOpenChartsModal = (location?: { x: number; y: number }) => {
    setIsChartsModalOpen(true);
    shouldReopenChartsModalRef.current = false;
    setPendingChartLocation(location || null);
  };



  const handleToggleJournal = async (journalId: string, isLinked: boolean) => {
    if (!id) return;
    try {
      if (isLinked) {
        await boardService.unlinkJournalFromBoard(id, journalId);
        deleteJournal(journalId);
      } else {
        await boardService.linkJournalToBoard(id, journalId);

        // Optimistic update: Find journal in journalStore and add to board journals
        const journalStore = (await import('../../stores/journalStore')).useJournalStore.getState();
        const journal = journalStore.journals.find(j => j.id === journalId);

        if (journal) {
          addJournal({
            ...journal,
            isEnabled: true,
            order: journals.length
          } as any);
        }

        queryClient.invalidateQueries({ queryKey: ["board-payload", id] });
}
    } catch (error) {
      console.error("Error toggling journal link:", error);
      showAlert({
        type: "error",
        message: t("errors.somethingWentWrong") || "Failed to link journal"
      });
    }
  };

  const handleOpenAddJournal = () => {
    setConfirmationModal({
      isOpen: true,
      title: "Quit Board?",
      message: "You are about to leave the current board to manage your money journals. Any unsaved layout changes will be lost.",
      confirmText: "Continue",
      variant: "primary",
      onConfirm: () => {
        setIsChannelEntityModalOpen(false);
        navigate("/boards?tab=money_journal&action=add");
      }
    });
  };

  const handleChartClickFromModal = (type: string) => {
    // Perform standard add chart action
    handleChartClick(type);

    // Close modal permanently (user can reopen if needed)
    setIsChartsModalOpen(false);
    shouldReopenChartsModalRef.current = false;
  };

  const handleChartClick = (type: string) => {
    // If board is in lock mode, automatically switch to build mode
    if (!isEditMode) {
      setSavedLayout([...layoutItems]);
      setIsEditMode(true);
    }

    const chartDef = CHART_DEFINITIONS.find((c) => c.type === type);
    if (!chartDef) return;

    const currentBp = detectBreakpoint(window.innerWidth);
    const newItemId = `${type}-${Date.now()}`;

    // Get the physical positions of items at the current breakpoint so we don't scramble their layout
    const currentLayouts = generateResponsiveLayouts(layoutItems);
    const physicallyPlacedItems = currentLayouts[currentBp] || layoutItems;

    // Find position for the new item using local skyline
    let finalPos = findPositionForNewItem(
      physicallyPlacedItems,
      chartDef.defaultSizeTier,
      chartDef.defaultAspectRatio,
      currentBp
    );

    if (pendingChartLocation) {
      // Find nearest non-colliding layout space starting from the clicked coord
      const colsMap: Record<string, number> = { xl: 24, lg: 24, md: 12, sm: 8, xs: 4 };
      const currentCols = colsMap[currentBp] || 12;

      let found = false;
      for (let y = pendingChartLocation.y; y < 100 && !found; y++) {
        for (let x = (y === pendingChartLocation.y ? pendingChartLocation.x : 0); x <= currentCols - finalPos.w; x++) {
          const hasCollision = physicallyPlacedItems.some(i =>
            x < i.x + i.w && x + finalPos.w > i.x &&
            y < i.y + i.h && y + finalPos.h > i.y
          );
          if (!hasCollision) {
            finalPos = { ...finalPos, x, y };
            found = true;
            break;
          }
        }
      }
    }

    const newItem: GridItem = {
      i: newItemId,
      type: type,
      sizeTier: chartDef.defaultSizeTier,
      aspectRatio: chartDef.defaultAspectRatio,
      order: physicallyPlacedItems.length,
      x: finalPos.x,
      y: finalPos.y,
      w: finalPos.w,
      h: finalPos.h,
      _editBp: currentBp,
    };

    setPendingChartLocation(null);

    // Mark all existing items with the current breakpoint AND their actual calculated positions
    const combined = [...physicallyPlacedItems.map((item: GridItem) => ({ ...item, _editBp: currentBp })), newItem];
    const sorted = [...combined].sort((a, b) => {
      if (a.y === b.y) return a.x - b.x;
      return a.y - b.y;
    });
    const newState = sorted.map((item: GridItem, index: number) => ({ ...item, order: index }));

    setLayoutItems(newState);

    // Handle history
    if (!isEditMode) {
      setHistory([[...layoutItems], newState]);
      setHistoryIndex(1);
    } else {
      addToHistory(newState);
    }

    // Scroll to and highlight the new chart
    setTimeout(() => {
      const chartElement = document.querySelector(`[data-chart-id="${newItemId}"]`);
      if (chartElement) {
        chartElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          chartElement.classList.add('chart-blink');
          setTimeout(() => {
            chartElement.classList.remove('chart-blink');
          }, 1000);
        }, 500);
      }
    }, 100);
  };

  const handleRemoveItem = (itemId: string) => {
    setConfirmationModal({
      isOpen: true,
      title: "Remove Chart",
      message: "Are you sure you want to remove this chart from the board?",
      onConfirm: async () => {
        const newItems = layoutItems.filter((item) => item.i !== itemId);
        setLayoutItems(newItems);

        if (isEditMode) {
          addToHistory(newItems);
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } else {
          // If not in edit mode, save immediately
          try {
            await saveBoardConfig(newItems);
            setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          } catch (error) {
            // Revert on error
            setLayoutItems(layoutItems);
          }
        }
      },
    });
  };



  // Initialize layout items from board data or active page
  useEffect(() => {
    let itemsToLoad: GridItem[] = [];

    if (activePageId && activePageId !== 'MAIN') {
      const activePage = pages.find(p => p.id === activePageId);
      if (activePage?.chartConfig && Array.isArray(activePage.chartConfig)) {
        itemsToLoad = convertStorageToGridItems(activePage.chartConfig);
      }
    } else {
      // Main board config (activePageId is 'MAIN' or null)
      if (board?.chartConfig && Array.isArray(board.chartConfig)) {
        itemsToLoad = convertStorageToGridItems(board.chartConfig);
      }
    }

    // Only update the working layout if we are NOT in edit mode.
    // This prevents losing unsaved layout changes when global board data
    // (like currency or date range) refreshes in the background.
    if (!isEditMode) {
      setLayoutItems([...itemsToLoad]);
    }

    setSavedLayout([...itemsToLoad]);

    // Reset edit state when switching pages
    if (activePageId !== lastActivePageIdRef.current) {
      setHistory([]);
      setHistoryIndex(-1);
      setIsEditMode(false);
      lastActivePageIdRef.current = activePageId;
    }
  }, [board, activePageId, pages, isEditMode]);

  const saveBoardConfig = async (items: GridItem[]) => {
    if (!id) return;

    try {
      // Save layout to backend
      // Convert runtime grid items to storage schema
      const storageConfig = convertGridItemsToStorage(items);

      if (activePageId && activePageId !== 'MAIN') {
        // Update specific page
        await boardService.updatePage(id, activePageId, {
          chartConfig: storageConfig,
        });

        // Update store with new config
        updatePage(activePageId, { chartConfig: storageConfig });
      } else {
        // Update board directly (Main View)
        await boardService.updateBoard(id, {
          chartConfig: storageConfig,
        });
      }

      setSavedLayout([...items]);
    } catch (error) {
      console.error("Error saving board layout:", error);
      showAlert({
        type: "error",
        title: t("errors.error") as string,
        message: "Failed to save board layout",
      });
      throw error;
    }
  };

  const handleSaveLayoutChanges = async () => {
    // Enforce max_charts_per_page limit before saving
    const maxCharts = getFeatureValue('max_charts_per_page');
    if (typeof maxCharts === 'number' && maxCharts !== -1 && layoutItems.length > maxCharts) {
      setUpgradeModal({ isOpen: true, featureKey: 'max_charts_per_page', currentUsage: layoutItems.length });
      return; // Stay in edit mode, don't reset changes
    }

    try {
      await saveBoardConfig(layoutItems);
      setIsEditMode(false);

      // Trigger onboarding for Composer pages on first edit save
      if (activePage?.type === 'Composer') {
      }
    } catch (error) {
      // Error handled in saveBoardConfig
    }
  };

  const handleCancelLayoutChanges = () => {
    // Check if there are changes
    const hasChanges = JSON.stringify(layoutItems) !== JSON.stringify(savedLayout);

    if (hasChanges) {
      setConfirmationModal({
        isOpen: true,
        title: "Discard Changes",
        message: "You have unsaved changes. Are you sure you want to discard them?",
        onConfirm: () => {
          setLayoutItems(savedLayout);
          setIsEditMode(false);
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        },
      });
    } else {
      setLayoutItems(savedLayout);
      setIsEditMode(false);
    }
  };

  const isCreatingPageRef = useRef(false);



  const handleOpenSaveTemplateModal = () => {
    setIsChartsModalOpen(false);
    setIsSaveTemplateModalOpen(true);
  }

  const handleSaveTemplateSuccess = () => {
    setIsSaveTemplateModalOpen(false);

  };

  // Auto-scroll logic for drag and drop
  const mainContentRef = useRef<HTMLDivElement>(null);
  const scrollSpeedRef = useRef(0);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMousePosRef = useRef<{ x: number; y: number } | null>(null);

  const handleAddPage = () => {
    // Enforce max_pages_per_board limit before allowing page creation
    const maxPages = getFeatureValue('max_pages_per_board');
    if (typeof maxPages === 'number' && maxPages !== -1 && pages.length >= maxPages) {
      setUpgradeModal({ isOpen: true, featureKey: 'max_pages_per_board', currentUsage: pages.length });
      return;
    }
    setIsPageTypeModalOpen(true);
  };

  const handleSelectPageType = async (type: PageType) => {
    if (!id) return;
    try {
      // Calculate new page name using translation
      const baseName = t(`templates.pageTypes.${type.toLowerCase().replace(' ', '')}`, type);
      let name = baseName;
      let counter = 1;
      while (pages.some(p => p.name === name)) {
        counter++;
        name = `${baseName} ${counter}`;
      }

      const newPage = await boardService.createPage(id, { name, type });
      if (newPage && newPage.id) {
        const isFirst = pages.length === 0;
        
        addPage(newPage);
        setActivePage(newPage.id);

        setIsPageTypeModalOpen(false);
        if (pageTypeRequiresTemplates(type)) {
          setIsTemplatesModalOpen(true);
        }

        if (isFirst) {
          if (type !== 'Composer') {
          }
        }

        // We set this to true so handleApplyTemplate knows it's a fresh page
        isCreatingPageRef.current = true;
      }
    } catch (error) {
      console.error("Failed to create page", error);
      showAlert({ type: "error", message: t("errors.somethingWentWrong") || "Failed" });
    }
  };



  const handleApplyTemplate = async (templateConfig: any[]) => {
    if (!id || !activePageId) return;

    try {
      const gridItems = convertStorageToGridItems(templateConfig);
      const storageConfig = convertGridItemsToStorage(gridItems);

      // Update page with template content
      await boardService.updatePage(id, activePageId, { chartConfig: storageConfig });

      // Update store
      updatePage(activePageId, { chartConfig: storageConfig });

      // Set layout items for the editor
      setLayoutItems(gridItems);

      // Enter edit mode relative to the new template
      setSavedLayout([...gridItems]);
      setIsEditMode(true);
      setHistory([[...gridItems]]);
      setHistoryIndex(0);

      // Reset creation flag
      isCreatingPageRef.current = false;

      // Close modal
      setIsTemplatesModalOpen(false);
    } catch (error) {
      console.error("Failed to apply template", error);
      showAlert({ type: "error", message: t("errors.somethingWentWrong") || "Failed" });
      isCreatingPageRef.current = false;
    }
  };




  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    scrollSpeedRef.current = 0;
    lastMousePosRef.current = null;
  };

  const handleDragScroll = (clientX: number, clientY: number) => {
    const container = mainContentRef.current;
    if (!container) return;

    lastMousePosRef.current = { x: clientX, y: clientY };

    const { top, bottom } = container.getBoundingClientRect();
    const threshold = 50;
    const maxSpeed = 20;

    // Calculate normalized position (0 to 1) within threshold
    let speed = 0;
    if (clientY < top + threshold) {
      // Near top - scroll up
      const intensity = 1 - Math.max(0, (clientY - top) / threshold);
      speed = -maxSpeed * intensity;
    } else if (clientY > bottom - threshold) {
      // Near bottom - scroll down
      const intensity = 1 - Math.max(0, (bottom - clientY) / threshold);
      speed = maxSpeed * intensity;
    }

    scrollSpeedRef.current = speed;

    if (speed !== 0 && !scrollIntervalRef.current) {
      scrollIntervalRef.current = setInterval(() => {
        const currentContainer = mainContentRef.current;
        if (!currentContainer) return;

        const currentSpeed = scrollSpeedRef.current;
        if (currentSpeed === 0) {
          stopAutoScroll();
          return;
        }

        // Check boundaries
        const maxScrollTop = currentContainer.scrollHeight - currentContainer.clientHeight;
        const newScrollTop = currentContainer.scrollTop + currentSpeed;

        if (currentSpeed > 0 && currentContainer.scrollTop >= maxScrollTop) {
          return;
        }

        if (currentSpeed < 0 && currentContainer.scrollTop <= 0) {
          return;
        }

        currentContainer.scrollTop = newScrollTop;

        // Dispatch a fake mousemove event to trigger RGL update
        // RGL listens to mousemove on the document/window
        if (lastMousePosRef.current) {
          const event = new MouseEvent('mousemove', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: lastMousePosRef.current.x,
            clientY: lastMousePosRef.current.y
          });
          document.dispatchEvent(event);
        }
      }, 16); // ~60fps
    } else if (speed === 0 && scrollIntervalRef.current) {
      stopAutoScroll();
    }
  };

  // Clean up interval on unmount
  useEffect(() => {
    return () => stopAutoScroll();
  }, []);

  const handleToggleChannelStatus = async (channel: BoardChannel, isActive: boolean) => {
    if (!id) return;
    setIsDataLoading(true);
    try {
      // Toggle the BoardChannel row directly. For pseudo siblings sharing one
      // real channel, this disables only the targeted row's scope.
      const response = await boardService.updateBoardChannelStatus(
        id,
        channel.boardChannelId ?? channel.id,
        isActive,
      );

      // Update store immediately with returned data for instant UI feedback
      if (response && response.data) {
        setBoardData(response.data);
      }

      // Maintain query consistency in background
      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
    } catch (error) {
      console.error('Failed to toggle channel status', error);
      showAlert({
        type: 'error',
        title: t('errors.error') as string,
        message: t('errors.failedToUpdateChannelStatus', 'Failed to update channel status') as string,
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleToggleJournalStatus = async (journalId: string, isActive: boolean) => {
    if (!id) return;
    setIsDataLoading(true);
    try {
      const response = await boardService.updateBoardJournalStatus(id, journalId, isActive);

      // Update store immediately with returned data
      if (response && response.data) {
        setBoardData(response.data);
      }

      queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
    } catch (error) {
      console.error('Failed to toggle journal status', error);
      showAlert({
        type: 'error',
        title: t('errors.error') as string,
        message: t('errors.failedToUpdateJournalStatus', 'Failed to update journal status') as string,
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  return (
    <Fragment>
      {/* Main Content Area */}
      <div className="h-[calc(100svh_-_var(--billing-banner-h,0px))] h-[calc(100dvh_-_var(--billing-banner-h,0px))] w-full flex flex-col bg-background-page dark:bg-neutral-950 min-w-0 relative transition-colors duration-300 overflow-hidden">
        {/* Mobile Header with Hamburger, Date Picker, and Connection Button */}
        {/* Main Content */}
        <main
          className="flex-1 flex flex-col min-h-0 relative overflow-hidden"
          onDragOver={(e) => {
            e.preventDefault(); // Necessary to allow dropping
            handleDragScroll(e.clientX, e.clientY);
          }}
          onDragLeave={() => stopAutoScroll()}
          onDrop={() => {
            // Stop scrolling when an item is dropped
            stopAutoScroll();
            // The actual drop logic is handled by BoardGrid onDrop, but the event bubbles up.
            // However, BoardGrid's onDrop might stop propagation?
            // RGL onDrop usually handles it.
          }}
        >
          {/* Main header in content area - Now visible on all screen sizes */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-2 px-4 md:px-2 pt-2 pb-0">

              {/* Exit/Back Button - Desktop */}
              <button
                onClick={handleBackToExplorer}
                disabled={isEditMode}
                className={`h-9 w-9 shrink-0 flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg transition-colors ${isEditMode ? 'opacity-50 pointer-events-none' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                title={t("boards.backToExplorer") || "Back to Explorer"}
              >
                <Logo className="w-5 h-5" />
              </button>

              {/* Edit Mode Actions - Desktop removed from here */}

              {/* Notice Button */}
              <AnimatePresence initial={false}>
                {(notices.length > 0) && (
                  <motion.div
                    layout={hasMounted}
                    className="overflow-hidden flex items-center shrink-0"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newState = !isStackedNoticesOpen;
                        setIsStackedNoticesOpen(newState);
                        setIsNoticeBarOpen(false);
                      }}
                      className={`h-9 w-9 shrink-0 flex items-center justify-center rounded-lg transition-all relative ${
                        isStackedNoticesOpen 
                          ? 'border border-accent-primary bg-accent-primary/5 dark:bg-accent-primary/10 shadow-[0_0_15px_rgba(var(--color-accent-primary),0.3)] text-accent-primary' 
                          : 'bg-neutral-50 dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
                      }`}
                      title={t("boards.viewNotice") || "View notice"}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                      </div>
                      {!(isNoticeBarOpen || isStackedNoticesOpen) && notices.length > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full border border-white dark:border-neutral-900"></span>
                      )}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div layout={hasMounted} className="flex-1 min-w-0 flex items-center h-9">
                <motion.div
                  ref={titleButtonRef}
                  layout={hasMounted}
                  transition={{ type: "spring", stiffness: 400, damping: 40 }}
                  onClick={() => {
                    if (isEditMode) return;
                    setShouldEditBoardName(false);
                    setIsContentModalOpen(true);
                  }}
                  className={`flex-1 min-w-0 h-full relative flex items-center justify-between text-sm font-bold text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg px-3 text-left transition-colors ${isEditMode ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center min-w-0 flex-none gap-1.5">
                    <span
                      onClick={(e) => {
                        if (isEditMode) return;
                        e.stopPropagation();
                        setShouldEditBoardName(true);
                        setIsContentModalOpen(true);
                      }}
                      className={`truncate text-neutral-800 dark:text-neutral-100 transition-colors ${isEditMode ? '' : 'hover:text-accent-primary cursor-pointer'}`}
                    >
                      {board?.name || boardNameFromState || ""}
                    </span>
                    <svg
                      onClick={(e) => {
                        if (isEditMode) return;
                        e.stopPropagation();
                        setShouldEditBoardName(false);
                        setIsContentModalOpen(true);
                      }}
                      className={`shrink-0 w-3 h-3 text-neutral-400 dark:text-neutral-500 transition-colors ${isEditMode ? '' : 'hover:text-accent-primary cursor-pointer'}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>

                    <AnimatePresence>
                      {/* Net Value Badge */}
                      {showNetValue && (finance?.total ?? 0) !== 0 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95, width: 0, marginLeft: -10 }}
                          transition={{ duration: 0.2 }}
                          className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold shadow-sm ring-1 ring-inset shrink-0 flex-nowrap whitespace-nowrap ${
                            (finance?.total || 0) >= 0 
                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20" 
                              : "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20"
                          }`}
                        >
                          {formatHeaderCompactNumber(finance?.total || 0, boardCurrency)}
                        </motion.div>
                      )}

                    </AnimatePresence>
                  </div>

                  {/* AI Trigger Area (Flexible space) — shows loader when finance is loading */}
                  <div
                    className={`flex-1 h-full min-w-0 flex items-center justify-center ${(isFinanceLoading || isDataLoading) ? '' : showAITriggerArea ? 'cursor-pointer' : 'opacity-0 pointer-events-none'}`}
                    onClick={(e) => {
                      if (isFinanceLoading || isDataLoading) return;
                      if (!showAITriggerArea || isEditMode) return;
                      e.stopPropagation();
                      if (!isAITaskBannerOpen) {
                        setAiMode('history');
                        setIsTaskApprovalMode(false);
                      }
                      setIsAITaskBannerOpen(true);
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {(isFinanceLoading || isDataLoading) ? (
                        <motion.div
                          key="title-loader"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-center h-full gap-1"
                        >
                          <div className="shrink-0">
                            <AnimatedLogo variant="processing" size="sm" className="scale-[0.6] origin-center" />
                          </div>
                          <BoardTitleLoadingText />
                        </motion.div>
                      ) : showAITriggerArea ? (
                        <motion.div
                          key="ai-trigger"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center gap-1.5 h-full group/ai"
                        >
                          <svg className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 group-hover/ai:text-accent-primary transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                          </svg>
                          <span className="text-[11px] font-bold text-neutral-400 dark:text-neutral-500 group-hover/ai:text-accent-primary transition-colors whitespace-nowrap">
                            {t("boards.ai.askDashbal", "Ask Dashbal AI")}
                          </span>
                          <span className="text-[9px] font-medium text-neutral-300 dark:text-neutral-600 whitespace-nowrap">
                            Space
                          </span>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>

                  {/* Date Range + Circular Icon Buttons row */}
                  <div className="flex items-center gap-1.5 ml-2 min-w-0 shrink-0">
                    {/* Date Range */}
                    <AnimatePresence>
                      {showDateRange && dateRange.startDate && dateRange.endDate && (
                        <DatePicker
                          value={dateRange}
                          onChange={handleDateRangeChange}
                          onOpen={() => setIsDatePickerOpen(true)}
                          onClose={() => setIsDatePickerOpen(false)}
                          align="right"
                          trigger={
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.95, width: 0, marginRight: -6 }}
                              transition={{ duration: 0.2 }}
                              className="flex items-center text-[10.5px] font-semibold text-neutral-500 dark:text-neutral-400 shrink-0 border-r border-neutral-200 dark:border-neutral-700 pr-2.5 flex-nowrap whitespace-nowrap hover:text-accent-primary cursor-pointer transition-colors"
                            >
                              {format(dateRange.startDate, 'MMM d')} - {format(dateRange.endDate, 'MMM d')}
                            </motion.div>
                          }
                        />
                      )}
                    </AnimatePresence>
                    {collapseConnections ? (
                      <div className="relative group/tooltip flex items-center justify-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); setIsContentModalOpen(true); }}
                          className="relative flex items-center justify-center w-6 h-6 rounded-full border border-neutral-200 dark:border-neutral-800 transition-all bg-white dark:bg-neutral-800 shadow-sm opacity-100"
                        >
                          <SafeImage src="https://cdn.dashbal.com/assets/connections.svg" className="w-3.5 h-3.5 object-contain" />
                        </button>
                        <div className="absolute top-[calc(100%+8px)] right-0 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] border border-white/10 after:content-[''] after:absolute after:-top-1 after:right-2 after:w-2 after:h-2 after:bg-neutral-900 after:rotate-45 after:border-l after:border-t after:border-white/10">
                          {t("boards.viewConnections") || "View Connections"}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Supported Channels */}
                        {supportedChannels.map(type => {
                          const typeConnections = connectionGroupsByType[type.id] || [];
                          const isConnected = typeConnections.length > 0;
                          const count = typeConnections.length;
                          
                          let tooltipText = type.name;
                          if (isConnected) {
                            if (count === 1) {
                              tooltipText = typeConnections[0].name;
                            } else if (count > 1) {
                              const namesList = typeConnections.map((c: { name: string }) => (c.name)).join(', ');
                              tooltipText = t('boards.connectionsCount', { count, name: type.name }) + `: ${namesList}`;
                            }
                          }

                          return (
                            <div key={type.id} className="relative group/tooltip flex items-center justify-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); isConnected ? setIsContentModalOpen(true) : setIsSelectConnectionTypeModalOpen(true); }}
                                className={`relative flex items-center justify-center w-6 h-6 rounded-full border border-neutral-200 dark:border-neutral-800 transition-all ${isConnected ? 'bg-white dark:bg-neutral-800 shadow-sm opacity-100' : 'bg-neutral-50 dark:bg-neutral-900 grayscale opacity-40 hover:opacity-70'}`}
                              >
                                <SafeImage src={type.icon} className="w-3.5 h-3.5 object-contain" />
                                {isConnected && count > 1 && (
                                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[12px] h-3 px-0.5 rounded-full bg-accent-primary text-[8px] font-bold text-white shadow-sm ring-1 ring-white dark:ring-neutral-900">
                                    {count}
                                  </span>
                                )}
                              </button>
                              <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] border border-white/10 after:content-[''] after:absolute after:-top-1 after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-2 after:bg-neutral-900 after:rotate-45 after:border-l after:border-t after:border-white/10">
                                {tooltipText}
                              </div>
                            </div>
                          );
                        })}

                        {/* Money Journal */}
                        <div className="relative group/tooltip flex items-center justify-center">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (journals.length > 0) {
                                setIsContentModalOpen(true);
                              } else {
                                handleQuitBoardToManageJournals('header');
                              }
                            }}
                            className={`relative flex items-center justify-center w-6 h-6 rounded-full border border-neutral-200 dark:border-neutral-800 transition-all ${journals.length > 0 ? 'bg-white dark:bg-neutral-800 shadow-sm opacity-100' : 'bg-neutral-50 dark:bg-neutral-900 grayscale opacity-40 hover:opacity-70'}`}
                          >
                            <SafeImage src={JOURNAL_CONFIG.icon} className="w-3.5 h-3.5 object-contain" />
                            {journals.length > 1 && (
                              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[12px] h-3 px-0.5 rounded-full bg-accent-primary text-[8px] font-bold text-white shadow-sm ring-1 ring-white dark:ring-neutral-900">
                                {journals.length}
                              </span>
                            )}
                          </button>
                          <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] border border-white/10 after:content-[''] after:absolute after:-top-1 after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-2 after:bg-neutral-900 after:rotate-45 after:border-l after:border-t after:border-white/10">
                            {journals.length === 1 ? journals[0].name : (journals.length > 1 ? t('boards.journalsCount', { count: journals.length }) : JOURNAL_CONFIG.name)}
                          </div>
                        </div>

                        {/* Board Spec */}
                        <div className="relative group/tooltip flex items-center justify-center">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (specs.length > 0) {
                                setIsContentModalOpen(true);
                              } else {
                                setEditingSpec(null);
                                setIsBoardSpecModalOpen(true);
                              }
                            }}
                            className={`relative flex items-center justify-center w-6 h-6 rounded-full border border-neutral-200 dark:border-neutral-800 transition-all ${specs.length > 0 ? 'bg-white dark:bg-neutral-800 shadow-sm opacity-100' : 'bg-neutral-50 dark:bg-neutral-900 grayscale opacity-40 hover:opacity-70'}`}
                          >
                            <SafeImage src={SPEC_CONFIG.icon} className="w-3.5 h-3.5 object-contain" />
                            {specs.length > 1 && (
                              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[12px] h-3 px-0.5 rounded-full bg-accent-primary text-[8px] font-bold text-white shadow-sm ring-1 ring-white dark:ring-neutral-900">
                                {specs.length}
                              </span>
                            )}
                          </button>
                          <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] border border-white/10 after:content-[''] after:absolute after:-top-1 after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-2 after:bg-neutral-900 after:rotate-45 after:border-l after:border-t after:border-white/10">
                            {specs.length === 1 ? specs[0].name : (specs.length > 1 ? t('boards.specsCount', { count: specs.length }) : SPEC_CONFIG.name)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>

                {/* AI Notice and Write Buttons */}
                <motion.div
                  layout={hasMounted}
                  className={`h-full flex-shrink-0 flex items-center whitespace-nowrap ml-2 transition-opacity duration-200 lg:hidden ${isEditMode ? 'opacity-40 pointer-events-none' : ''}`}
                >
                  <div className="flex items-center h-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg p-0 shadow-sm relative overflow-hidden">
                    <button
                      id="onboarding-ai-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Reset to history mode when explicitly clicking the AI button in the header (mobile/scrolled view)
                        if (!isAITaskBannerOpen) {
                          setAiMode('history');
                          setIsTaskApprovalMode(false);
                        }
                        setIsAITaskBannerOpen(!isAITaskBannerOpen);
                      }}
                    className="flex items-center justify-center w-9 h-full flex-shrink-0 aspect-square transition-colors duration-200 relative text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300"
                    title={String(t("boards.ai.title"))}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                      <path d="M5 3v4" />
                      <path d="M19 17v4" />
                      <path d="M3 5h4" />
                      <path d="M17 19h4" />
                    </svg>
                  </button>
                </div>
              </motion.div>

                <AnimatePresence initial={false} mode="popLayout">
                  {isEditMode ? (
                    <motion.div
                      key="edit-actions"
                      layoutId="edit-actions-toggle"
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: "auto", opacity: 1 }}
                      exit={{ width: 0, opacity: 0, transition: { duration: 0.2 } }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      className="h-full flex-shrink-0 overflow-hidden whitespace-nowrap ml-2"
                    >
                      <div className="flex items-center gap-1 h-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg px-0.5">
                        {(!activePageId || activePageId === 'MAIN') ? null : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenChartsModal();
                              }}
                              className="flex items-center justify-center w-9 h-full flex-shrink-0 aspect-square text-neutral-500 hover:text-accent-primary hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
                              title={t("actions.addCharts") || "Add Charts"}
                            >
                              <Plus className="w-4 h-4 stroke-[2.5px]" />
                            </button>
                            <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-800" />
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditModeSave();
                          }}
                          className="flex items-center justify-center w-9 h-full flex-shrink-0 aspect-square text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                          title={t("actions.save") || "Save changes"}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-800" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditModeCancel();
                          }}
                          className="flex items-center justify-center w-9 h-full flex-shrink-0 aspect-square text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                          title={t("actions.cancel") || "Cancel changes"}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ) : (pages.length > 0 && (!activePageId || activePageId === 'MAIN' || pages.find((p: any) => p.id === activePageId)?.type === 'Composer')) ? (
                    <motion.div
                      key="start-edit"
                      layoutId="edit-actions-toggle"
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: "auto", opacity: 1 }}
                      exit={{ width: 0, opacity: 0, transition: { duration: 0.2 } }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      className="h-full flex-shrink-0 overflow-hidden whitespace-nowrap ml-2"
                    >
                      <div className="flex items-center h-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg p-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsEditMode(true);
                          }}
                          className="flex items-center justify-center w-9 h-full flex-shrink-0 aspect-square text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
                          title={t("actions.edit") || "Edit page"}
                        >
                          <Pencil className="w-4 h-4 stroke-[2.5px]" />
                        </button>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>

              <div className={`flex items-center gap-2 shrink-0 transition-opacity ${isEditMode ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* Desktop Currency Button */}
                <BoardCurrencyButton
                  onClick={() => setIsCurrencyModalOpen(true)}
                  className="h-9 w-9 flex-shrink-0 aspect-square"
                />
                <DatePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  onOpen={() => setIsDatePickerOpen(true)}
                  onClose={() => setIsDatePickerOpen(false)}
                  align="right"
                  iconOnly
                  buttonClassName="h-9 w-9 flex-shrink-0 aspect-square"
                  isHidden={showDateRange && !!dateRange.startDate && !!dateRange.endDate}
                />
                <BoardSetupSteps
                  hasAITask={previousAITasks.length > 0}
                  isContentModalOpen={isContentModalOpen}
                  isHidden={isDatePickerOpen || isAITaskBannerOpen}
                  onOpenAddConnection={() => setIsSelectConnectionTypeModalOpen(true)}
                  onOpenAddJournal={() => handleQuitBoardToManageJournals('onboarding')}
                  onOpenAddSpec={() => setIsBoardSpecModalOpen(true)}
                  onOpenCreatePage={() => setIsPageTypeModalOpen(true)}
                  onOpenAI={() => setIsAITaskBannerOpen(true)}
                  onShowFeatureExplanation={(type) => setFeatureExplanation({ isOpen: true, type })}
                />
              </div>


            </div>

            {/* Board Main Space Container */}
<div className="mt-2 flex-1 min-h-0 flex flex-col px-4 md:px-2 relative">
              {/* Stacked Notice Banners Overlay - MOVED HERE to align with board content top */}
              <div className="absolute inset-0 pointer-events-none z-[40]">
                <StackedNoticeBanners
                  isVisible={isStackedNoticesOpen}
                  notices={notices}
                  onDelete={(index) => {
                    removeNotice(index);
                  }}
                  onExpand={(_index) => {
                    setIsNoticeBarOpen(true);
                  }}
                />
              </div>
              {/* AI Task Banner Overlay */}
              <AnimatePresence>
                {isAITaskBannerOpen && (
                  <>
                    {/* Dark Background Overlay */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setIsAITaskBannerOpen(false)}
                      className="fixed inset-0 z-40 bg-transparent"
                    />

                    <motion.div
                      initial={{ height: 0, opacity: 0, y: -10 }}
                      animate={{ height: '100%', opacity: 1, y: 0 }}
                      exit={{ height: 0, opacity: 0, y: -10 }}
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      className="absolute inset-0 z-50 pointer-events-none dark"
                    >
                      <div
                        onClick={() => {
                          if (!isTaskApprovalMode) setIsAITaskBannerOpen(false);
                        }}
                        className="bg-neutral-900/95 border border-white/10 rounded-lg shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] pointer-events-auto flex flex-col overflow-hidden h-full relative"
                      >
                        {/* Header Area */}
                        <div className="absolute top-0 w-full z-30 p-4 flex justify-between pointer-events-none">
                          <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                            <AnimatePresence>
                              {aiMode === 'conversation' && (
                                <motion.button
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  onClick={() => {
                                    setAiMode('history');
                                    setAiPrompt("");
                                    setIsTaskApprovalMode(false);
                                  }}
                                  className="p-2 hover:bg-white/10 rounded-md transition-colors text-white/30 hover:text-white"
                                  title={String(t("boards.ai.backToHistory"))}
                                >
                                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                  </svg>
                                </motion.button>
                              )}
                            </AnimatePresence>
                          </div>

                          <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setIsAITaskBannerOpen(false)}
                              className="hidden lg:block p-2 hover:bg-white/10 rounded-md transition-colors text-white/30 hover:text-white"
                              title={String(t("actions.close"))}
                            >
                              <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f6969c83.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f6969c83.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </button>
                          </div>
                        </div>

                        {/* Content Area */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`flex-1 w-full ${isTaskApprovalMode ? 'max-w-none' : 'max-w-4xl'} mx-auto flex flex-col pt-16 pb-6 px-4 min-h-0 transition-all duration-300`}
                        >
                          <AnimatePresence mode="wait">
                            {aiMode === 'history' ? (
                              <motion.div
                                key="history-mode"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex-1 flex flex-col min-h-0"
                              >
                                {/* Pinned Prompt in History Mode */}
                                <div className="shrink-0 w-full max-w-3xl mx-auto z-30 pb-6">
                                  <div className="relative border border-white/10 rounded-2xl bg-[#2f2f2f] focus-within:ring-1 focus-within:ring-white/10 transition-all shadow-lg">
                                    <div className="flex items-end px-4 py-[10px] min-h-[40px]">
                                      <textarea
                                        ref={aiTextAreaRef}
                                        rows={1}
                                        value={aiPrompt}
                                        onChange={(e) => {
                                          setAiPrompt(e.target.value);
                                          if (e.target.value.length > 0) setHasAIInputChanged(true);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === ' ' && !hasAIInputChanged) {
                                            e.preventDefault();
                                            setIsAITaskBannerOpen(false);
                                          } else if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendAITask();
                                          }
                                        }}
                                        placeholder={String(t("boards.ai.promptPlaceholder"))}
                                        className="w-full bg-transparent border-none p-0 m-0 text-[15px] leading-[20px] text-white placeholder-white/40 focus:outline-none transition-all resize-none scrollbar-hide"
                                        style={{ height: '20px' }}
                                        autoFocus
                                      />
                                      <div className="w-8 ml-2 flex-shrink-0" /> {/* Spacer for button */}
                                      <button
                                        onClick={() => handleSendAITask()}
                                        disabled={!aiPrompt.trim()}
                                        className={`absolute right-2 bottom-1 p-2 rounded-lg transition-all flex items-center justify-center w-8 h-8 ${aiPrompt.trim() ? 'bg-white text-black hover:bg-white/90 shadow-sm' : 'bg-white/5 text-white/20'}`}
                                      >
                                        <svg className="w-4 h-4 ml-[-2px] mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Scrollable History Section */}
                                <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                                  {/* Pinned Title Section */}
                                  {previousAITasks.length > 0 && (
                                    <div className="shrink-0 w-full max-w-3xl mx-auto px-1 mb-3">
                                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                                        {t("boards.ai.history")}
                                      </h4>
                                    </div>
                                  )}

                                  <div
                                    className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
                                    onScroll={(e) => {
                                      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                                      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMoreHistory && !loadingHistory) {
                                        setHistoryPage(p => p + 1);
                                      }
                                    }}
                                  >
                                    <div className="w-full max-w-3xl mx-auto space-y-4 pb-4">
                                      {/* Previous Tasks */}
                                      {previousAITasks.length > 0 && (
                                        <div
                                          className="space-y-3"
                                        >
                                          {Array.isArray(previousAITasks) && previousAITasks.map(task => (
                                            <div
                                              key={task.id}
                                              onClick={() => {
                                                setAiMode('conversation');
                                                setActiveTaskId(task.id);
                                                setIsTaskApprovalMode(false);
                                                // We rely on the useEffect(..., [activeTaskId]) to fetch messages now
                                              }}
                                              className="group bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/[0.07] transition-all cursor-pointer flex items-center gap-3"
                                            >
                                              <div className="shrink-0 text-accent-primary/40">
                                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_e4509ed9.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_e4509ed9.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                              </div>
                                              <div className="flex-1 min-w-0 py-0.5">
                                                <p className="text-sm text-white/70 line-clamp-2">{task.prompt}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                  {task.status === 'loading' ? (
                                                    <p className="text-[10px] text-accent-primary uppercase tracking-tighter">Processing...</p>
                                                  ) : task.status === 'cancelled' ? (
                                                    <p className="text-[10px] text-rose-500 uppercase tracking-tighter">Interrupted</p>
                                                  ) : (
                                                    <p className="text-[10px] text-white/30 uppercase tracking-tighter">Completed</p>
                                                  )}
                                                  {task.timestamp && (
                                                    <>
                                                      <span className="w-1 h-1 rounded-full bg-white/10" />
                                                      <p className="text-[10px] text-white/20">{new Date(task.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                              {task.status === 'loading' ? (
                                                <div className="shrink-0 flex items-center justify-center relative ml-4">
                                                  {/* Spinning ring */}
                                                  <div className="absolute inset-0 w-8 h-8 m-auto">
                                                    <svg className="w-full h-full animate-[spin_2s_linear_infinite] text-accent-primary" viewBox="0 0 50 50">
                                                      <circle
                                                        className="opacity-20"
                                                        cx="25" cy="25" r="20"
                                                        fill="none" stroke="currentColor" strokeWidth="4"
                                                      />
                                                      <circle
                                                        className="opacity-100"
                                                        cx="25" cy="25" r="20"
                                                        fill="none" stroke="currentColor" strokeWidth="4"
                                                        strokeLinecap="round"
                                                        strokeDasharray="90 150"
                                                        strokeDashoffset="0"
                                                      />
                                                    </svg>
                                                  </div>
                                                  {/* Stop interaction button */}
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleStopTask(task.id);
                                                    }}
                                                    className="relative z-10 w-8 h-8 rounded-full flex flex-col items-center justify-center cursor-pointer group/stop focus:outline-none"
                                                    title={String(t("boards.ai.stopTask"))}
                                                  >
                                                    <div className="w-2.5 h-2.5 bg-white/40 rounded-[2px] transition-all duration-200 group-hover/stop:bg-rose-500 group-hover/stop:scale-110" />
                                                  </button>
                                                </div>
                                              ) : (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteTask(task);
                                                  }}
                                                  className="shrink-0 flex items-center justify-center ml-2 p-2 rounded-md transition-colors text-white/20 hover:text-rose-500 hover:bg-rose-500/10 opacity-100 focus:outline-none"
                                                  title={String(t("boards.ai.deleteConversation"))}
                                                >
                                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                                                  </svg>
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ) : (
                              <motion.div
                                key="conversation-mode"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex-1 flex flex-col min-h-0"
                              >
                                {/* Scrollable Conversation List & Task Approval Mode */}
                                <div className={`flex-1 flex flex-col md:flex-row min-h-0 ${isTaskApprovalMode ? 'mb-0 pb-0 md:mb-4 md:pb-2' : 'mb-4 pb-2'} relative ${isTaskApprovalMode ? 'md:overflow-hidden' : 'overflow-hidden'}`}>
                                  {/* Left Panel: Conversation */}
                                  <motion.div
                                    layout
                                    style={{ width: isTaskApprovalMode ? (window.innerWidth >= 768 ? '50%' : '100%') : '100%' }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="flex flex-col min-h-0"
                                  >
                                    <div
                                      onScroll={(e) => {
                                        const container = e.currentTarget;
                                        container.classList.add('scrolling');
                                        const existingTimeout = container.dataset.scrollTimeout;
                                        if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                                        const timeoutId = setTimeout(() => {
                                          container.classList.remove('scrolling');
                                        }, 1000);
                                        container.dataset.scrollTimeout = timeoutId.toString();

                                        // Inverse scroll logic (since it's flex-col-reverse or we start from bottom)
                                        // But wait, the container uses flex-col-reverse.
                                        // In flex-col-reverse, scrollTop is negative in some browsers or simply counts from bottom = 0 in others (actually `e.currentTarget.scrollTop` might be negative or decreasing).
                                        // Wait, typical flex-col-reverse in Tailwind is just visually reversed. React standard DOM handles `scrollTop`.
                                        // Let's use the simplest approach for flex-col-reverse:
                                        // In standard implementation of flex-col-reverse, the top of the container is actually the END of the content.  
                                        // Math.abs(scrollTop) gets larger as you scroll "up" (visually).

                                        const { scrollTop, scrollHeight, clientHeight } = container;

                                        // If we get close to the top boundary (meaning we've scrolled up to view older messages)
                                        // Note: in flex-col-reverse, scrollTop starts at 0 and goes negative OR goes from max down to 0 depending on browser. Let's just catch either case broadly:
                                        const distanceToTop = Math.abs(scrollTop); // Most reliable proxy
                                        const maxScroll = scrollHeight - clientHeight;
                                        if (distanceToTop >= maxScroll - clientHeight * 0.5) {
                                          if (hasMoreMessages && !loadingMessages && activeTaskId) {
                                            const currentPage = messagesPageByTask[activeTaskId] || 1;
                                            fetchMessages(activeTaskId, currentPage + 1);
                                          }
                                        }
                                      }}
                                      className="flex-1 overflow-y-auto fade-scrollbar scrollbar-dark flex flex-col-reverse px-2 w-full max-w-4xl mx-auto pt-16 md:pt-2"
                                    >
                                      <div className="flex flex-col w-full max-w-3xl mx-auto space-y-8 pb-4">
                                        {(() => {
                                          const visibleMessages = currentConversation.filter((m: any) =>
                                            (m.role === 'user' || m.role === 'assistant') &&
                                            !(m.role === 'user' && m.content === AI_APPROVAL_USER_MARKER)
                                          );
                                          return visibleMessages.map((msg, idx) => (
                                          <div key={msg.id} className="flex flex-col items-start gap-1">
                                            {msg.role === 'user' ? (
                                              <div className="flex items-center gap-3 self-end mb-2 w-full justify-end">
                                                <div className="bg-[#2f2f2f] rounded-2xl px-5 py-3 text-[15px] text-white/90 max-w-[80%] leading-relaxed shadow-sm block markdown-content">
                                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {msg.content}
                                                  </ReactMarkdown>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="flex items-start w-full">
                                                <div className="flex-1 min-w-0 pt-1 text-white/90 text-[15px] leading-relaxed">
                                                  {msg.content === AI_ERROR_SENTINEL ? (
                                                    <div className="flex items-start gap-2">
                                                      <AlertCircle className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" />
                                                      <div className="flex-1 flex flex-wrap items-center gap-2 text-white/70">
                                                        <span>{String(t("boards.ai.errorMessage"))}</span>
                                                        {idx === visibleMessages.length - 1 && (
                                                          <button
                                                            type="button"
                                                            onClick={() => handleSendAITask("Continue", undefined, { isRetry: true })}
                                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 transition-colors"
                                                            aria-label={String(t("boards.ai.retry"))}
                                                          >
                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                            {String(t("boards.ai.retry"))}
                                                          </button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : msg.content ? (
                                                    <div className="markdown-content">
                                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {msg.content}
                                                      </ReactMarkdown>
                                                      {msg.isTaskApproval && idx === visibleMessages.length - 1 && (
                                                        <button
                                                          onClick={() => {
                                                            if (isTaskApprovalMode) {
                                                              setIsTaskApprovalMode(false);
                                                            } else {
                                                              setIsTaskApprovalMode(true);
                                                              const tasks = msg.approvalTasks;
                                                              let parsedTasks = [];
                                                              if (Array.isArray(tasks)) {
                                                                parsedTasks = tasks;
                                                              } else if (typeof tasks === 'string') {
                                                                try {
                                                                  const p = JSON.parse(tasks);
                                                                  if (Array.isArray(p)) parsedTasks = p;
                                                                } catch (e) {
                                                                  console.error("Failed to parse approvalTasks", e);
                                                                }
                                                              }
                                                              setApprovalTasks(parsedTasks);
                                                            }
                                                          }}
                                                          className={`mt-3 px-3 py-1.5 border rounded-md text-sm transition-colors ${isTaskApprovalMode ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/70'}`}
                                                        >
                                                          {(() => {
                                                            const tasksRaw = msg.approvalTasks;
                                                            let parsed: any[] = [];
                                                            if (Array.isArray(tasksRaw)) {
                                                              parsed = tasksRaw;
                                                            } else if (typeof tasksRaw === 'string') {
                                                              try { const p = JSON.parse(tasksRaw); if (Array.isArray(p)) parsed = p; } catch (e) {}
                                                            }
                                                            const allAccepted = parsed.length > 0 && parsed.every(p => p.accepted);
                                                            if (allAccepted) {
                                                              return isTaskApprovalMode
                                                                ? String(t("boards.ai.hideAcceptedTasks", "Hide tasks"))
                                                                : String(t("boards.ai.viewAcceptedTasks", "View completed tasks"));
                                                            }
                                                            return isTaskApprovalMode
                                                              ? String(t("boards.ai.hideReview"))
                                                              : String(t("boards.ai.reviewTasks"));
                                                          })()}
                                                        </button>
                                                      )}
                                                      {msg.isQuotaError && idx === visibleMessages.length - 1 && (
                                                        <button
                                                          onClick={() => {
                                                            window.open(shopifyManageUrl || '/plan', '_blank', 'noopener,noreferrer');
                                                          }}
                                                          className="mt-3 flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm transition-colors border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 pointer-events-auto"
                                                        >
                                                          {shopifyManageUrl ? <ShoppingBag className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                                                          {String(shopifyManageUrl
                                                            ? t("billing.managedByShopify.openShopify", "Manage in Shopify")
                                                            : t("upgrade.cta", "Upgrade Plan"))}
                                                        </button>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="flex items-center h-5 opacity-60">
                                                      <AnimatedLogo variant="processing" size="sm" className="scale-[0.6] origin-left" />
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          ));
                                        })()}
                                      </div>
                                    </div>
                                  </motion.div>

                                  {/* Divider */}
                                  <AnimatePresence>
                                    {isTaskApprovalMode && (
                                      <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                        transition={{ duration: 0.5 }}
                                        className="hidden md:block w-px bg-white/10 self-stretch z-10"
                                      />
                                    )}
                                  </AnimatePresence>

                                  {/* Right Panel: Task Approval */}
                                  <AnimatePresence mode="popLayout">
                                    {isTaskApprovalMode && (
                                      <>
                                        {/* Mobile Backdrop */}
                                        <motion.div
                                          initial={{ opacity: 0 }}
                                          animate={{ opacity: 1 }}
                                          exit={{ opacity: 0 }}
                                          onClick={() => setIsTaskApprovalMode(false)}
                                          className="md:hidden absolute inset-0 z-30"
                                        />

                                        <motion.div
                                          initial={{ x: '100%', opacity: 0, width: window.innerWidth >= 768 ? 0 : '85%' }}
                                          animate={{ x: 0, opacity: 1, width: window.innerWidth >= 768 ? '50%' : '85%' }}
                                          exit={{ x: '100%', opacity: 0, width: window.innerWidth >= 768 ? 0 : '85%' }}
                                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                          className="absolute md:relative inset-y-0 right-[-1rem] md:right-0 md:inset-auto z-40 flex flex-col min-h-0 overflow-hidden bg-neutral-900 md:bg-transparent border-l border-white/10 md:border-l-0 rounded-tr-lg top-[-4rem] h-[calc(100%+4rem)] md:top-0 md:h-full"
                                        >
                                          <motion.div
                                            initial={{ x: -40, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            exit={{ x: -40, opacity: 0 }}
                                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                            className="flex-1 flex flex-col min-h-0 w-full"
                                          >
                                            <div
                                              onScroll={(e) => {
                                                const container = e.currentTarget;
                                                container.classList.add('scrolling');
                                                const existingTimeout = container.dataset.scrollTimeout;
                                                if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                                                const timeoutId = setTimeout(() => {
                                                  container.classList.remove('scrolling');
                                                }, 1000);
                                                container.dataset.scrollTimeout = timeoutId.toString();
                                              }}
                                              className="flex-1 overflow-y-auto fade-scrollbar scrollbar-dark pl-6 md:px-2 w-full max-w-4xl mx-auto pt-16 md:pt-2"
                                            >
                                              <div className="w-full max-w-3xl mx-auto">
                                                {(() => {
                                                  const tasksArr = Array.isArray(approvalTasks) ? approvalTasks : [];
                                                  const acceptedItems = tasksArr.filter(t => t.accepted);
                                                  const pendingItems = tasksArr.filter(t => !t.accepted);
                                                  const allAccepted = tasksArr.length > 0 && pendingItems.length === 0;
                                                  return (
                                                    <>
                                                      <h3 className="text-white text-lg font-medium mb-4">
                                                        {allAccepted
                                                          ? t("boards.ai.acceptedTasksTitle", "Completed Tasks")
                                                          : t("boards.ai.reviewTasksTitle")}
                                                      </h3>
                                                      <p className="text-white/60 text-sm mb-4">
                                                        {allAccepted
                                                          ? t("boards.ai.acceptedTasksDescription", "These actions were applied automatically. They are shown here for visibility.")
                                                          : t("boards.ai.selectTasksDescription")}
                                                      </p>
                                                      {acceptedItems.length > 0 && (
                                                        <div className="mb-3">
                                                          {pendingItems.length > 0 && (
                                                            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">
                                                              {t("boards.ai.acceptedSection", "Already Done")}
                                                            </p>
                                                          )}
                                                          <div className="space-y-2">
                                                            {acceptedItems.map(task => {
                                                              const failed = task.status === 'error';
                                                              return (
                                                                <div
                                                                  key={task.id}
                                                                  className={`flex items-start gap-3 p-3 rounded-lg border ${failed ? 'border-rose-500/20 bg-rose-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`}
                                                                >
                                                                  <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${failed ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                                                    {failed ? (
                                                                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                                                                      </svg>
                                                                    ) : (
                                                                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                      </svg>
                                                                    )}
                                                                  </span>
                                                                  <div className="flex-1 min-w-0">
                                                                    <span className="text-sm text-white">{task.text}</span>
                                                                    <span className={`ml-2 text-[10px] uppercase tracking-wider ${failed ? 'text-rose-400' : 'text-emerald-400'}`}>
                                                                      {failed ? t("boards.ai.taskFailed", "Failed") : t("boards.ai.taskDone", "Done")}
                                                                    </span>
                                                                  </div>
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        </div>
                                                      )}
                                                      {pendingItems.length > 0 && (
                                                        <div>
                                                          {acceptedItems.length > 0 && (
                                                            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">
                                                              {t("boards.ai.pendingSection", "Awaiting Approval")}
                                                            </p>
                                                          )}
                                                          <div className="space-y-2">
                                                            {pendingItems.map(task => (
                                                              <label key={task.id} className="flex items-start gap-3 p-3 rounded-lg border border-white/5 bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
                                                                <input
                                                                  type="checkbox"
                                                                  checked={task.checked}
                                                                  onChange={(e) => {
                                                                    setApprovalTasks(prev => {
                                                                      if (!Array.isArray(prev)) return [];
                                                                      return prev.map(at => at.id === task.id ? { ...at, checked: e.target.checked } : at);
                                                                    });
                                                                  }}
                                                                  className="mt-1 flex-shrink-0"
                                                                />
                                                                <span className={`text-sm ${task.checked ? 'text-white' : 'text-white/40'}`}>
                                                                  {task.text}
                                                                </span>
                                                              </label>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </>
                                                  );
                                                })()}
                                              </div>
                                            </div>
                                            <div className="pt-4 flex justify-end gap-2 shrink-0 border-t border-white/10 mb-4 w-full max-w-4xl mx-auto px-2">
                                              <button
                                                onClick={() => setIsTaskApprovalMode(false)}
                                                className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                                              >
                                                {(Array.isArray(approvalTasks) ? approvalTasks : []).some(t => !t.accepted)
                                                  ? t("actions.cancel")
                                                  : t("actions.close", "Close")}
                                              </button>
                                              {(Array.isArray(approvalTasks) ? approvalTasks : []).some(t => !t.accepted) && (
                                                <button
                                                  onClick={() => handleValidateTasks()}
                                                  className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-sm text-white/70 transition-colors"
                                                >
                                                  {t("boards.ai.validateExecute")}
                                                </button>
                                              )}
                                            </div>
                                          </motion.div>
                                        </motion.div>
                                      </>
                                    )}
                                  </AnimatePresence>
                                </div>

                                {/* Pinned Prompt in Conversation Mode (at the bottom) */}
                                <div
                                  className="shrink-0 w-full max-w-3xl mx-auto z-[60]"
                                  onClick={() => {
                                    if (isTaskApprovalMode) {
                                      setIsTaskApprovalMode(false);
                                    }
                                  }}
                                >
                                  <div className="relative border border-white/10 rounded-2xl bg-[#2f2f2f] focus-within:ring-1 focus-within:ring-white/10 transition-all shadow-lg">
                                    <div className="flex items-end px-4 py-[10px] min-h-[40px]">
                                      <textarea
                                        ref={aiTextAreaRef}
                                        rows={1}
                                        value={aiPrompt}
                                        onChange={(e) => {
                                          setAiPrompt(e.target.value);
                                          if (e.target.value.length > 0) setHasAIInputChanged(true);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === ' ' && !hasAIInputChanged) {
                                            e.preventDefault();
                                            setIsAITaskBannerOpen(false);
                                          } else if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            const convIsLoading = currentConversation.length > 0 &&
                                              currentConversation[currentConversation.length - 1].role === 'assistant' &&
                                              !currentConversation[currentConversation.length - 1].content;
                                            if (!convIsLoading) {
                                              handleSendAITask();
                                            }
                                          }
                                        }}
                                        placeholder={t('boards.ai.messagePlaceholder')}
                                        className="w-full bg-transparent border-none p-0 m-0 text-[15px] leading-[20px] text-white placeholder-white/40 focus:outline-none transition-all resize-none scrollbar-hide"
                                        style={{ height: '20px' }}
                                        autoFocus
                                      />
                                      <div className="w-8 ml-2 flex-shrink-0" /> {/* Spacer for button */}
                                      <button
                                        onClick={() => {
                                          const convIsLoading = currentConversation.length > 0 &&
                                            currentConversation[currentConversation.length - 1].role === 'assistant' &&
                                            !currentConversation[currentConversation.length - 1].content;

                                          if (convIsLoading) {
                                            const taskId = previousAITasks.find((t: any) => t.status === 'loading')?.id;
                                            if (taskId) handleStopTask(taskId);
                                          } else {
                                            handleSendAITask();
                                          }
                                        }}
                                        disabled={!(currentConversation.length > 0 && currentConversation[currentConversation.length - 1].role === 'assistant' && !currentConversation[currentConversation.length - 1].content) && !aiPrompt.trim()}
                                        className={`absolute right-2 bottom-1 p-2 rounded-lg transition-all flex items-center justify-center w-8 h-8 ${currentConversation.length > 0 && currentConversation[currentConversation.length - 1].role === 'assistant' && !currentConversation[currentConversation.length - 1].content ? 'bg-rose-500/20 text-rose-500 hover:bg-rose-500/30 shadow-sm' : (aiPrompt.trim() ? 'bg-white text-black hover:bg-white/90 shadow-sm' : 'bg-white/5 text-white/20')}`}
                                      >
                                        {currentConversation.length > 0 && currentConversation[currentConversation.length - 1].role === 'assistant' && !currentConversation[currentConversation.length - 1].content ? (
                                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="6" y="6" width="12" height="12" rx="2" />
                                          </svg>
                                        ) : (
                                          <svg className="w-4 h-4 ml-[-2px] mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>

              <AITaskConfirmationModal
                isOpen={aiTaskConfirmationModal.isOpen}
                title={aiTaskConfirmationModal.title}
                message={aiTaskConfirmationModal.message}
                confirmText={aiTaskConfirmationModal.confirmText}
                cancelText={aiTaskConfirmationModal.cancelText}
                onConfirm={aiTaskConfirmationModal.onConfirm}
                onCancel={() => setAiTaskConfirmationModal(prev => ({ ...prev, isOpen: false }))}
              />





              {/* Notice Banner Overlay */}
              <AnimatePresence>
                {isNoticeBarOpen && activeNoticeIndex !== null && notices[activeNoticeIndex] && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => {
                          setIsNoticeBarOpen(false);
                      }}
                      className="fixed inset-0 z-40 bg-transparent"
                    />

                    <motion.div
                      initial={{ height: 0, opacity: 0, y: -10 }}
                      animate={{ height: 'auto', opacity: 1, y: 0 }}
                      exit={{ height: 0, opacity: 0, y: -10 }}
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      className="fixed md:absolute inset-0 z-50 pointer-events-none dark"
                    >
                      <div className="bg-neutral-900/95 border border-white/10 rounded-lg shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] pointer-events-auto flex flex-col overflow-hidden max-h-full">
                        {/* Banner Header with Close button - more compact */}
                        <div className="flex justify-between items-center px-4 pt-3 pb-1.5 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-white/5 rounded-lg flex items-center justify-center border border-white/10">
                              <span className="w-4 h-4 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c06efcc0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c06efcc0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </div>
                            <span className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Notice</span>
                          </div>
                          <button
                            onClick={() => {
                                setIsNoticeBarOpen(false);
                            }}
                            className="p-1 hover:bg-white/10 rounded-md transition-colors text-white/30 hover:text-white"
                            title="Close"
                          >
                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f6969c83.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f6969c83.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          </button>
                        </div>

                        {/* Banner Content */}
                        <div className="px-6 py-4 overflow-y-auto custom-scrollbar scrollbar-dark">
                          <p className="text-white text-[14px] leading-relaxed whitespace-pre-wrap font-medium">
                            {notices[activeNoticeIndex]?.text}
                          </p>
                        </div>

                        {/* Banner Footer (Subtle fade at bottom) */}
                        <div className="h-2 bg-gradient-to-t from-black/20 to-transparent shrink-0" />
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>

              <div className="flex-1 min-h-0 relative flex flex-col">
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={activePageId || 'MAIN'}
                    initial={animationProps.initial}
                    animate={animationProps.animate}
                    exit={animationProps.exit}
                    transition={animationProps.transition}
                    className="flex-1 flex flex-col min-h-0"
                  >
                    {(() => {
                      const hasData = !!finance;
                      return (
                        <BoardMainSpace
                          ref={mainContentRef}
                          className="flex-1"
                          disableScroll={Boolean(!activePageId || activePageId === 'MAIN' || isCustomPage(pages.find((p: any) => p.id === activePageId)?.type))}
                          fullPage={Boolean(isCustomPage(pages.find((p: any) => p.id === activePageId)?.type))}
                          isEditMode={isEditMode}
                          isLoading={isFinanceLoading || isDataLoading}
                          isInitialLoading={isDataLoading && (!boardFullResponse.isSuccess || !hasData)}
                          isTimeRangeExceeded={boardFullResponse.data?.data?.timeRangeExceeded}
                        >
                          {pages.length === 0 ? (
                        <div className="relative w-full max-w-5xl mx-auto h-full mt-4">
                          <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl flex items-center justify-center mb-6 text-neutral-400">
                              <span className="w-8 h-8" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_b370cc4b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b370cc4b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </div>
                            <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
                              {channels.length === 0 && journals.length === 0
                                ? t("boards.emptyStateNoDataTitle") || "Connect Your Data"
                                : t("boards.emptyBoardTitle") || "Start Building Your Board"}
                            </h3>
                            <p className="text-sm text-neutral-400 dark:text-neutral-500 max-w-sm mb-6">
                              {channels.length === 0 && journals.length === 0
                                ? t("boards.emptyStateNoDataDescription") || "Connect your channels or add journals to start visualizing your finance."
                                : t("boards.emptyBoardDescription") || "You don't have any pages yet. Create a page from a template to get started visualizing your data."}
                            </p>
                            <button
                              onClick={() => {
                                if (channels.length === 0 && journals.length === 0) {
                                  setIsSelectConnectionTypeModalOpen(true);
                                } else {
                                  handleAddPage();
                                }
                              }}
                              className="group flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-accent-primary dark:hover:text-accent-primary border border-dashed border-neutral-300 dark:border-neutral-700 hover:border-accent-primary/50 rounded-xl transition-all duration-200 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10"
                            >
                              <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_6511fb89.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6511fb89.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                              {channels.length === 0 && journals.length === 0
                                ? t("boards.connectData") || "Connect Data"
                                : t("boards.createPage") || "Create Page"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <PageRenderer
                          pageType={activePageId ? pages.find((p: any) => p.id === activePageId)?.type : undefined}
                          isEditMode={isEditMode}
                          board={board}
                          items={safeLayoutItems}
                          isChartLoading={isDataLoading}
                          isChartError={Object.keys(channelErrors || {}).length > 0}
                          onChartErrorClick={() => setIsContentModalOpen(true)}
                          onLayoutChange={() => { }} // We handle updates via drag/resize/drop events explicitly to avoid loops
                          onDragStop={handleDragStop}
                          onResizeStop={handleResizeStop}
                          onDrop={handleDrop}
                          onRemoveItem={handleRemoveItem}
                          droppingItem={droppingItem}
                          onDrag={(_layout: Layout[], _oldItem: Layout, newItem: Layout) => {
                            // Center the dropping placeholder on the cursor
                            if (newItem.i === '__dropping-elem__') {
                              newItem.x = Math.max(0, newItem.x - Math.floor(newItem.w / 2));
                              newItem.y = Math.max(0, newItem.y - Math.floor(newItem.h / 2));
                            }
                          }}
                          onAddCharts={handleOpenChartsModal}
                          onToggleEditMode={setIsEditMode}
                        />
                      )}
                    </BoardMainSpace>
                  );
                })()}
              </motion.div>
            </AnimatePresence>
              </div>

              {/* Pagination floating at the bottom */}
              {
                pages.length > 0 && (
                  <div className="mt-auto pt-2 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] md:pb-2">
                    <BoardPagination
                      pages={pages}
                      activePageId={activePageId}
                      onSelectPage={handleSelectPage}
                      onAddPage={handleAddPage}
                      isLoading={false}
                      onUpdatePage={handleUpdatePage}
                      onDeletePage={handleDeletePage}
                      onReorderPages={handleReorderPages}
                    />
                  </div>
                )
              }
            </div >
          </div >
        </main >
      </div >

      {/* Unified Channel/Entity Selection Modal */}
      < ChannelEntityModal
        isOpen={isChannelEntityModalOpen}
        onClose={handleCloseChannelEntityModal}
        onValidate={handleValidateEntitySelection}
        onAddNewChannel={handleOpenAddChannel}
        activeChannel={activeEntityChannel}
        rootEntityId={activeEntityRootId}
        activeJournal={activeEntityJournal}
        onSelectChannel={handleSelectChannel}
        selectedEntityIds={workingSelectedEntityIds}
        onToggleEntity={handleToggleEntity}
        boardChannels={workingChannels as any[]}
        initialView={channelEntityModalView as any}
        boardId={id || ""}
        linkedJournalIds={Array.isArray(journals) ? journals.map(c => c.id) : []}
        onToggleJournal={handleToggleJournal}
        onAddJournal={handleOpenAddJournal}
        selectedOperationIds={workingSelectedOperationIds}
        onToggleOperation={handleToggleOperation}
        onValidateOperations={handleValidateOperationSelection}
        viewOnly={channelEntityModalViewOnly}
        boardCurrency={boardCurrency}
        currencyConversions={currencyConversions}
        onBack={handleChannelEntityModalBack}
        highlightedEntityId={activeEntityHighlightId}
        shouldHighlight={shouldHighlightEntity}
        onClearHighlight={handleClearEntityHighlight}
      />

      {/* Pseudo-Channel Picker Modal — select which top-level entity (e.g. ad
          account) to attach as a pseudo-channel. Each pick creates one
          BoardChannel row on the backend. */}
      <PseudoChannelPickerModal
        isOpen={isPseudoPickerOpen}
        onClose={() => {
          setIsPseudoPickerOpen(false);
          if (returnToContentModal) {
            setIsContentModalOpen(true);
            setReturnToContentModal(false);
          }
        }}
        channel={pseudoPickerChannel}
        addedEntityIds={channels
          .filter((c) => c.isPseudoChannel && c.channelId === pseudoPickerChannel?.id && c.sourceEntityId)
          .map((c) => c.sourceEntityId as string)}
        onPick={handlePseudoChannelPick}
      />

      {/* Board Content Modal */}
      <BoardContentModal
        isOpen={isContentModalOpen && !isCurrencyModalOpen}
        onToggleChannelStatus={handleToggleChannelStatus}
        onToggleJournalStatus={handleToggleJournalStatus}
        onClose={() => {
          if (isCurrencyModalOpen || upgradeModal.isOpen) return;
          setIsContentModalOpen(false);
          // Delay resetting board name edit state to avoid flicker during dismissal
          setTimeout(() => {
            setShouldEditBoardName(false);
          }, 300);
          // Auto-opening template modal logic removed per user requirement
        }}
        boardName={board?.name || 'Untitled Board'}
        onBoardNameUpdate={handleBoardNameUpdate}
        onAddConnection={() => {
          setIsContentModalOpen(false);
          setIsSelectConnectionTypeModalOpen(true);
        }}
        supportedChannels={supportedChannels}
        onSelectChannel={(c) => {
          if (!c) return;
          // ChannelEntityModal handles both normal and pseudo BoardChannel rows.
          // For pseudos, the modal scopes the entity tree to the row's source entity.
          setIsContentModalOpen(false);
          setReturnToContentModal(true);
          setChannelEntityModalViewOnly(false);
          handleOpenChannelEntityModal(c);
        }}
        onSelectJournal={(journal) => {
          setIsContentModalOpen(false);
          setReturnToContentModal(true);
          handleOpenJournalDetails(journal);
        }}
        isFinanceLoading={isFinanceLoading || isDataLoading}
        isFinanceError={isFinanceError}
        autoFocusTitle={shouldEditBoardName}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onCurrencyClick={() => setIsCurrencyModalOpen(true)}
        onAddSpec={() => {
          completeStep(OnboardingStep.ADD_SPEC);
          setIsContentModalOpen(false);
          setReturnToContentModal(true);
          setEditingSpec(null);
          setIsBoardSpecModalOpen(true);
        }}
        onEditSpec={(spec) => {
          setIsContentModalOpen(false);
          setReturnToContentModal(true);
          setEditingSpec(spec);
          setIsBoardSpecModalOpen(true);
        }}
        onDeleteSpec={handleDeleteSpec}
        onRemoveChannel={handleRemoveChannel}
        onRemoveJournal={handleRemoveJournal}
        onOpenExplanation={(type) => {
          setIsContentModalOpen(false);
          setReturnToContentModal(true);
          setFeatureExplanation({ isOpen: true, type });
        }}
      />

      <BoardSpecModal
        isOpen={isBoardSpecModalOpen}
        onClose={() => {
          setIsBoardSpecModalOpen(false);
          // Delay clearing spec to avoid flicker during dismissal
          setTimeout(() => {
            setEditingSpec(null);
          }, 300);
          if (returnToContentModal) {
            setIsContentModalOpen(true);
            setReturnToContentModal(false);
          }
        }}
        onBack={() => {
          setIsBoardSpecModalOpen(false);
          // Delay clearing spec to avoid flicker during dismissal
          setTimeout(() => {
            setEditingSpec(null);
          }, 300);
          if (returnToContentModal) {
            setIsContentModalOpen(true);
            setReturnToContentModal(false);
          }
        }}
        boardId={id || ''}
        spec={editingSpec}
      />

      <FeatureExplanationModal
        isOpen={featureExplanation.isOpen}
        onClose={() => {
          setFeatureExplanation(prev => ({ ...prev, isOpen: false }));
          if (returnToContentModal) {
            setIsContentModalOpen(true);
            setReturnToContentModal(false);
          }
        }}
        type={featureExplanation.type}
      />

      {/* Add Channel Modal */}
      <AddChannelModal
        isOpen={isAddChannelModalOpen}
        onClose={handleCloseAddChannel}
        onComplete={handleAddChannelComplete}
      />

      {/* Board Charts Modal */}
      <BoardChartsModal
        isOpen={isChartsModalOpen}
        onClose={() => setIsChartsModalOpen(false)}
        onChartClick={handleChartClickFromModal}
        onChartDragStart={handleChartDragStartFromModal}
        onChartDragEnd={handleChartDragEnd}
        isHidden={isChartsModalHidden}
        onSaveTemplate={handleOpenSaveTemplateModal}
      />

      {/* Board Templates Modal */}
      <BoardTemplatesModal
        isOpen={isTemplatesModalOpen}
        onClose={() => setIsTemplatesModalOpen(false)}
        onApplyTemplate={handleApplyTemplate}
      />

      <PageTypeSelectionModal
        isOpen={isPageTypeModalOpen}
        onClose={() => setIsPageTypeModalOpen(false)}
        onSelectType={handleSelectPageType}
      />

      <BoardSaveTemplateModal
        isOpen={isSaveTemplateModalOpen}
        onClose={() => setIsSaveTemplateModalOpen(false)}
        onSaveSuccess={handleSaveTemplateSuccess}
        currentChartConfig={safeLayoutItems}
      />

      {/* Board Currency Modal */}
      <BoardCurrencyModal
        isOpen={isCurrencyModalOpen}
        onClose={() => setIsCurrencyModalOpen(false)}
        boardId={id || ""}
        defaultCurrency={boardCurrency}
        otherCurrencies={usedCurrencies}
        conversions={currencyConversions}
        onSave={handleCurrencySave}
      />


      {/* Select Connection Type Modal */}
      <SelectConnectionTypeModal
        isOpen={isSelectConnectionTypeModalOpen}
        onClose={() => setIsSelectConnectionTypeModalOpen(false)}
        onBack={() => {
          setIsSelectConnectionTypeModalOpen(false);
          setIsContentModalOpen(true);
        }}
        onSelectChannel={() => {
          setIsSelectConnectionTypeModalOpen(false);
          setIsSelectChannelSourceModalOpen(true);
        }}
        onSelectJournal={() => {
          setIsSelectConnectionTypeModalOpen(false);
          setIsSelectJournalSourceModalOpen(true);
        }}
      />

      {/* Select Channel Source Modal */}
      <SelectChannelSourceModal
        isOpen={isSelectChannelSourceModalOpen}
        onClose={() => setIsSelectChannelSourceModalOpen(false)}
        onBack={() => setIsSelectConnectionTypeModalOpen(true)}
        onSelectChannel={(channel) => {
          handleAutoAddChannel(channel as any);
        }}
        onAddNew={() => {
          setIsSelectChannelSourceModalOpen(false);
          setIsAddChannelModalOpen(true);
        }}
        boardChannels={channels as any}
      />

      {/* Select Journal Source Modal */}
      <SelectJournalSourceModal
        isOpen={isSelectJournalSourceModalOpen}
        onClose={() => setIsSelectJournalSourceModalOpen(false)}
        onBack={() => setIsSelectConnectionTypeModalOpen(true)}
        onSelectJournal={(journal) => {
          handleAutoAddJournal(journal);
        }}
        onAddNew={() => handleCreateAndOpenJournal()}
        boardJournals={journals || []}
      />

      {/* In-board journal details modal (editable, minimal mode) */}
      <BoardJournalDetailsModal
        isOpen={isJournalDetailsModalOpen}
        onClose={() => {
          // Headless UI Dialog treats clicks inside the EditJournalNameModal
          // (rendered as a sibling at higher z-index) as outside-clicks. Ignore
          // those so saving a name doesn't dismiss the details modal.
          if (pendingNameJournal) return;
          handleCloseJournalDetailsModal();
        }}
        boardId={id || ''}
        selectedJournal={activeJournalForDetails}
        isCreating={isCreatingJournal}
        onOpenInFullView={activeJournalForDetails && activeJournalForDetails.id !== '__pending__'
          ? () => handleOpenJournalInFullView(activeJournalForDetails.id)
          : undefined}
        onOperationCreated={handleJournalOperationCreatedOnBoard}
      />

      {/* One-time rename prompt shown after auto-creating a journal */}
      <EditJournalNameModal
        isOpen={!!pendingNameJournal}
        journalId={pendingNameJournal?.id || ''}
        currentName={pendingNameJournal?.name || ''}
        onClose={() => {
          const renamedJournalId = pendingNameJournal?.id;
          setPendingNameJournal(null);
          if (renamedJournalId) {
            // Pick up any name change from useJournalStore so the details-modal
            // header AND BoardContentModal reflect the new name immediately,
            // without waiting for a board-payload refetch.
            const updated = useJournalStore.getState().journals.find(j => j.id === renamedJournalId);
            if (updated) {
              useBoardStore.getState().updateJournal(renamedJournalId, { name: updated.name });
              setActiveJournalForDetails(prev => prev && prev.id === renamedJournalId
                ? { ...prev, name: updated.name }
                : prev);
            }
          }
          // Invalidate the board payload so subsequent fetches pick up the
          // fresh name on the backend side too (cache has been busted by
          // notifyJournalUpdate).
          if (id) {
            queryClient.invalidateQueries({ queryKey: ['board-payload', id] });
          }
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText || t("actions.remove") || "Remove"}
        cancelText={confirmationModal.cancelText || t("actions.cancel") || "Cancel"}
        variant={confirmationModal.variant || "danger"}
        onConfirm={confirmationModal.onConfirm}
        onCancel={() => {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          confirmationModal.onCancel?.();
        }}
      />

      {/* Plan Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal(prev => ({ ...prev, isOpen: false }))}
        featureKey={upgradeModal.featureKey}
        currentUsage={upgradeModal.currentUsage}
      />


      {/* Date Range Upgrade Modal */}
      <UpgradeModal
        isOpen={dateRangeUpgrade.isOpen}
        onClose={closeDateRangeUpgrade}
        featureKey="max_time_range_days"
        currentUsage={dateRangeUpgrade.currentDays}
      />

      {/* Sync Error Modal */}
      <InfoModal
        isOpen={syncErrorModal.isOpen}
        onClose={() => setSyncErrorModal({ isOpen: false })}
        title={t('syncErrorModal.title', { channelName: syncErrorModal.channelName })}
        description={t('syncErrorModal.message', { channelName: syncErrorModal.channelName }) + ' ' + t('syncErrorModal.suggestion')}
      />

    </Fragment >
  );
};

export default BoardPage;
